import { useState, useEffect } from "react";
import axios from "axios";
import { Card, Grid, Select, MenuItem, FormControl, InputLabel, CircularProgress, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, IconButton, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { API_ENDPOINTS } from "config/api";
import { useAuth } from "context/AuthContext";

const ATTACHMENT_FIELD_ID = "fldnielwPiUjUDrp1ul";
const GLOBAL_HISTORY_TABLE_ID = "tbllxFsIqInCDhYIgPK";
const GLOBAL_ITEMS_TABLE_ID = "tbl5L6sBe405YR3spyc";
const GLOBAL_CATEGORY_TABLE_ID = "tbleUomXN9VrpgOBj0G";

/**
 * CENTRALIZED TEABLE API MANAGER
 */
const teableAPI = {
  sanitize: (id) => String(id || "").split(':')[0].trim(),

  baseUrl: () => {
    const envBase = process.env.REACT_APP_TEABLE_BASE_URL;
    let base = (envBase || "https://teable.namuve.com").replace(/\/+$/, "");
    if (!base.startsWith('http')) {
      base = `https://${base}`;
    }
    return base;
  },

  getToken: (providedToken) => {
    return providedToken || process.env.REACT_APP_TEABLE_BEARER_TOKEN;
  },

  getHeaders: (token) => ({
    'Authorization': `Bearer ${teableAPI.getToken(token)?.trim()}`,
    'Content-Type': 'application/json'
  }),

  // GET a single record
  getRecord: async (tableId, recordId, token) => {
    const url = `${teableAPI.baseUrl()}/api/table/${teableAPI.sanitize(tableId)}/record/${teableAPI.sanitize(recordId)}`;
    console.log(`[TEABLE API] GET RECORD: ${url}`);
    return axios.get(url, { headers: teableAPI.getHeaders(token) });
  },

  // PATCH records (always use BULK for reliability)
  patchRecords: async (tableId, records, token) => {
    const url = `${teableAPI.baseUrl()}/api/table/${teableAPI.sanitize(tableId)}/record`;
    const payload = {
      records: records.map(r => ({
        id: teableAPI.sanitize(r.id),
        fields: r.fields
      }))
    };
    console.log(`[TEABLE API] PATCH ${tableId}: ${url}`, JSON.stringify(payload, null, 2));
    return axios.patch(url, payload, { headers: teableAPI.getHeaders(token) });
  },

  // GET table fields (schema)
  getFields: async (tableId, token) => {
    const url = `${teableAPI.baseUrl()}/api/table/${teableAPI.sanitize(tableId)}/field`;
    return axios.get(url, { headers: teableAPI.getHeaders(token) });
  },

  // POST records (always use BULK for reliability)
  postRecords: async (tableId, records, token) => {
    const url = `${teableAPI.baseUrl()}/api/table/${teableAPI.sanitize(tableId)}/record`;
    const payload = {
      records: records.map(r => ({
        fields: r.fields
      }))
    };
    console.log(`[TEABLE API] POST ${tableId}:`, JSON.stringify(payload, null, 2));
    return axios.post(url, payload, { headers: teableAPI.getHeaders(token) });
  },

  // NEW: Robust GET multiple records with pagination
  getRecords: async (tableId, token, params = {}) => {
    const cleanId = teableAPI.sanitize(tableId);
    const url = `${teableAPI.baseUrl()}/api/table/${cleanId}/record`;
    console.log(`[TEABLE API] GET RECORDS: ${url}`, { params, hasToken: !!teableAPI.getToken(token) });
    return axios.get(url, {
      params: { take: 1000, ...params },
      headers: teableAPI.getHeaders(token)
    });
  },

  // Upload attachment (Force-identify using Field ID mode)
  uploadAttachment: async (tableId, recordId, fieldId, file, token) => {
    const encodedFieldId = encodeURIComponent(String(fieldId || ATTACHMENT_FIELD_ID).trim());
    const url = `${teableAPI.baseUrl()}/api/table/${teableAPI.sanitize(tableId)}/record/${teableAPI.sanitize(recordId)}/${encodedFieldId}/uploadAttachment`;
    const formData = new FormData();
    formData.append('file', file);
    console.log(`[TEABLE API] UPLOAD ATTACHMENT (ID MODE): ${url}`);
    return axios.post(url, formData, {
      headers: {
        'Authorization': `Bearer ${token?.trim()}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

const sanitizeId = teableAPI.sanitize;

function Inventory({ standalone = true }) {
  const { user, isViewOnly, isCustom, hasPermission, isUser } = useAuth();

  // Check if user has view-only access to inventory
  const isInventoryViewOnly = isViewOnly() ||
    (isCustom() && hasPermission('inventory', 'view') && !hasPermission('inventory', 'complete'));

  const [apartments, setApartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState("All Properties");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [searchTerm, setSearchTerm] = useState("");
  const [detectedAttachmentField, setDetectedAttachmentField] = useState(null);

  const [loading, setLoading] = useState(true);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  // Auto-detect the attachment field from live data
  useEffect(() => {
    if (items.length > 0 && !detectedAttachmentField) {
      // Find an item that actually HAS attachments to use as a template
      const templateItem = items.find(it => {
        const f = it.fields;
        return Object.keys(f).some(key => {
          const val = f[key];
          return Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && (val[0].id || val[0].path || val[0].token);
        });
      });

      if (templateItem) {
        const fieldKey = Object.keys(templateItem.fields).find(key => {
          const val = templateItem.fields[key];
          return Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && (val[0].id || val[0].path || val[0].token);
        });
        if (fieldKey) {
          console.log("[DEBUG GLOBAL] Found attachment field:", fieldKey);
          setDetectedAttachmentField(fieldKey);
        }
      } else if (items[0]) {
        // FALLBACK: If no item has attachments yet, try to find the key by name only
        const anyItem = items[0];
        const backupKey = Object.keys(anyItem.fields).find(k => {
          const ck = k.toLowerCase().trim();
          return ck === "attachments" || ck === "images" || ck === "photos" || k.startsWith("fld");
        });
        if (backupKey) {
          console.log("[DEBUG GLOBAL] Backup match found:", backupKey);
          setDetectedAttachmentField(backupKey);
        }
      }
    }
  }, [items, detectedAttachmentField]);

  const showMessage = (message, severity = "info") => {
    // Show all messages including success, but specifically ensure errors are visible
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Auto-refresh function
  const [imageBlobs, setImageBlobs] = useState({});

  const AuthenticatedImage = ({ src, alt, style, onClick, className }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let isMounted = true;
      const fetchImage = async () => {
        if (!src) return;

        // If it's already a blob or data URL, use it
        if (src.startsWith('blob:') || src.startsWith('data:')) {
          setBlobUrl(src);
          setLoading(false);
          return;
        }

        // Helper to format URL properly
        const formatUrl = (u) => {
          const strUrl = String(u);
          if (strUrl.startsWith('http')) return strUrl;
          let baseUrl = (process.env.REACT_APP_TEABLE_BASE_URL || "https://teable.namuve.com").replace(/\/+$/, "");
          if (!baseUrl.startsWith('http')) baseUrl = `https://${baseUrl}`;
          const nakedBase = baseUrl.replace(/^https?:\/\//, '');
          if (strUrl.startsWith(nakedBase)) return `https://${strUrl}`;
          return `${baseUrl}${strUrl.startsWith('/') ? '' : '/'}${strUrl}`;
        };

        try {
          const finalSrc = formatUrl(src);
          const token = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

          const reqConfig = { responseType: 'blob' };
          // Only attach Bearer Header if the URL doesn't contain a built-in token
          if (!finalSrc.includes('token=')) {
            reqConfig.headers = token ? { Authorization: `Bearer ${token?.trim()}` } : {};
          }

          const response = await axios.get(finalSrc, reqConfig);

          if (isMounted) {
            const url = URL.createObjectURL(response.data);
            setBlobUrl(url);
            setLoading(false);
          }
        } catch (err) {
          console.error("Failed to load authenticated image:", err);
          if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }
      };

      fetchImage();
      return () => {
        isMounted = false;
        if (blobUrl && !src.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      };
    }, [src]);

    if (loading) return <MDBox sx={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f2f5' }}><CircularProgress size={20} /></MDBox>;
    if (error) return <MDBox sx={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}><span className="material-icons" style={{ color: '#adb5bd' }}>error_outline</span></MDBox>;

    return (
      <img
        src={blobUrl}
        alt={alt}
        style={style}
        onClick={onClick}
        className={className}
      />
    );
  };

  // ======== Category Modal States ========
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [nextCategoryId, setNextCategoryId] = useState(1);

  // ======== Item Modal States ========
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [newItemListingName, setNewItemListingName] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("");
  const [newItemQuantity, setNewItemQuantity] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("pcs");
  const [newItemCost, setNewItemCost] = useState("");
  const [newItemAttachment, setNewItemAttachment] = useState("");
  const [newItemStatus, setNewItemStatus] = useState("Good");
  const [isSubmittingItem, setIsSubmittingItem] = useState(false);
  const [nextItemId, setNextItemId] = useState(1);
  const [nextHistoryId, setNextHistoryId] = useState(1);

  // ======== Damage Modal States ========
  const [isDamageModalOpen, setIsDamageModalOpen] = useState(false);
  const [selectedItemForDamage, setSelectedItemForDamage] = useState(null);
  const [damageReservationId, setDamageReservationId] = useState("");
  const [damageDescription, setDamageDescription] = useState("");
  const [damageAttachments, setDamageAttachments] = useState([]);
  const [isSubmittingDamage, setIsSubmittingDamage] = useState(false);

  // ======== History Modal States ========
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState(null);
  const [itemHistoryRecords, setItemHistoryRecords] = useState([]);
  const [isMisplacedModalOpen, setIsMisplacedModalOpen] = useState(false);
  const [isFoundModalOpen, setIsFoundModalOpen] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [selectedItemForMisplaced, setSelectedItemForMisplaced] = useState(null);
  const [selectedItemForFound, setSelectedItemForFound] = useState(null);
  const [selectedItemForReplace, setSelectedItemForReplace] = useState(null);
  const [misplacedDescription, setMisplacedDescription] = useState("");
  const [foundDescription, setFoundDescription] = useState("");
  const [replaceDescription, setReplaceDescription] = useState("");
  const [replacementItemName, setReplacementItemName] = useState("");
  const [replaceStatus, setReplaceStatus] = useState("Good");
  const [misplacedAttachments, setMisplacedAttachments] = useState([]);
  const [foundAttachments, setFoundAttachments] = useState([]);
  const [replaceAttachments, setReplaceAttachments] = useState([]);
  const [isSubmittingMisplaced, setIsSubmittingMisplaced] = useState(false);
  const [isSubmittingFound, setIsSubmittingFound] = useState(false);
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false);
  const [isRetireModalOpen, setIsRetireModalOpen] = useState(false);
  const [selectedItemForRepair, setSelectedItemForRepair] = useState(null);
  const [selectedItemForRetire, setSelectedItemForRetire] = useState(null);
  const [repairDescription, setRepairDescription] = useState("");
  const [retireDescription, setRetireDescription] = useState("");
  const [repairPrice, setRepairPrice] = useState("");
  const [repairSendTo, setRepairSendTo] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [isSubmittingRetire, setIsSubmittingRetire] = useState(false);

  // ======== History Edit Modal States ========
  const [isEditHistoryModalOpen, setIsEditHistoryModalOpen] = useState(false);
  const [selectedRecordForEdit, setSelectedRecordForEdit] = useState(null);
  const [editHistoryStatus, setEditHistoryStatus] = useState("");
  const [editHistoryDescription, setEditHistoryDescription] = useState("");
  const [editHistoryPrice, setEditHistoryPrice] = useState("");
  const [editHistorySendTo, setEditHistorySendTo] = useState("");
  const [editHistoryReservationId, setEditHistoryReservationId] = useState("");
  const [editHistoryAttachments, setEditHistoryAttachments] = useState([]);
  const [editHistoryNewFiles, setEditHistoryNewFiles] = useState([]);
  const [isSubmittingEditHistory, setIsSubmittingEditHistory] = useState(false);
  const [editHistoryError, setEditHistoryError] = useState(null);

  // ======== Item Edit Modal States ========
  const [isEditItemModalOpen, setIsEditItemModalOpen] = useState(false);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [editItemListingName, setEditItemListingName] = useState("");
  const [editItemName, setEditItemName] = useState("");
  const [editItemCategory, setEditItemCategory] = useState("");
  const [editItemQuantity, setEditItemQuantity] = useState("");
  const [editItemCost, setEditItemCost] = useState("");
  const [editItemAttachments, setEditItemAttachments] = useState([]);
  const [editItemNewFiles, setEditItemNewFiles] = useState([]);
  const [isSubmittingEditItem, setIsSubmittingEditItem] = useState(false);
  const [editItemError, setEditItemError] = useState(null);

  // ======== Category Manage/Edit Modal States ========
  const [categoryRecords, setCategoryRecords] = useState([]);
  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [isSubmittingEditCategory, setIsSubmittingEditCategory] = useState(false);

  // ======== Dynamic Reservation Logic ========
  const [misplacedReservationId, setMisplacedReservationId] = useState("");
  const [recentReservations, setRecentReservations] = useState([]);
  const [isFetchingReservations, setIsFetchingReservations] = useState(false);

  const fetchReservationsForListing = async (listingName) => {
    if (!listingName) return;
    setIsFetchingReservations(true);
    setRecentReservations([]);
    try {
      const TEABLE_BASE_URL = process.env.REACT_APP_TEABLE_BASE_URL || "https://teable.namuve.com";
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const RESERVATIONS_TABLE_ID = "tbliOdo8ldmMO8rrYyN";
      const PAGE_SIZE = 100;

      // Dynamic filtering logic: only fetch what's needed directly from Teable
      const filters = {
        filter: {
          conjunction: "and",
          children: [
            {
              fieldId: "Listing Name", // Using name based matching for the filter formula
              operator: "contains",
              value: listingName
            }
          ]
        }
      };

      // Using the exact dynamic search URL format provided
      const response = await axios.get(
        `${TEABLE_BASE_URL}/api/table/${RESERVATIONS_TABLE_ID}/record`,
        {
          headers: { 'Authorization': `Bearer ${teableToken}` },
          params: { 
            search: [listingName, "Listing Name", "true"], // Correct format for the search[0]=val&search[1]=Listing+Name&search[2]=true URL
            take: 50
          }
        }
      );
      
      const all = response.data.records || [];
      
      console.log(`[fetchReservations] URL search found for "${listingName}": ${all.length}`, all);

      const filtered = all.filter(r => {
        const fields = r.fields || {};
        
        // 1. PROPERTY MATCHING
        const normalize = (s) => String(s || "").toLowerCase().replace(/\s+/g, "").trim();
        const target = normalize(listingName);
        const recordListing = normalize(fields["Listing Name"] || fields["Apartment Name"] || fields["Pakistan Listing Name"] || fields["Property"]);
        
        console.log(`[fetchReservations] Compare: "${recordListing}" vs "${target}"`);

        // Inclusion match covers things like "4F-41 (2B)" matching "4F-41"
        if (recordListing !== target && !recordListing.includes(target) && !target.includes(recordListing)) return false;

        // 2. STATUS MATCHING - STRICT
        const statusKeys = Object.keys(fields).filter(k => k.toLowerCase().includes("status"));
        const statusValues = statusKeys.map(k => String(fields[k] || "").toLowerCase());
        
        // ONLY allow "Check out" or "Staying guest"
        // Explicitly exclude "upcoming"
        const isStaying = statusValues.some(v => v.includes("stay") && !v.includes("upcoming"));
        const isCheckout = statusValues.some(v => v.includes("out") && !v.includes("upcoming"));

        return isStaying || isCheckout;
      });

      filtered.sort((a, b) => new Date(b.fields["Arrival Date"] || 0) - new Date(a.fields["Arrival Date"] || 0));
      
      // Limit to the single latest matching reservation only
      const latestOnly = filtered.length > 0 ? [filtered[0]] : [];
      
      console.log(`[fetchReservations] Final filtered count: ${latestOnly.length}`);
      setRecentReservations(latestOnly);
    } catch (e) {
      if (e?.response?.status !== 403) {
        console.error("Error fetching reservations:", e?.response?.data || e.message);
      }
    } finally {
      setIsFetchingReservations(false);
    }
  };

  const [newItemAttachments, setNewItemAttachments] = useState([]);
  const [repairAttachments, setRepairAttachments] = useState([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmittingRepair, setIsSubmittingRepair] = useState(false);
  const [isSubmittingReplace, setIsSubmittingReplace] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPreviewImage = async () => {
      if (!isPreviewModalOpen || previewImages.length === 0) return;

      setIsLoadingPreview(true);
      setPreviewBlobUrl(null);

      const url = previewImages[currentPreviewIndex];

      // Helper to format URL properly
      const formatUrl = (u) => {
        const strUrl = String(u);
        if (strUrl.startsWith('http')) return strUrl;
        let baseUrl = (process.env.REACT_APP_TEABLE_BASE_URL || "https://teable.namuve.com").replace(/\/+$/, "");
        if (!baseUrl.startsWith('http')) baseUrl = `https://${baseUrl}`;
        const nakedBase = baseUrl.replace(/^https?:\/\//, '');
        if (strUrl.startsWith(nakedBase)) return `https://${strUrl}`;
        return `${baseUrl}${strUrl.startsWith('/') ? '' : '/'}${strUrl}`;
      };

      const finalUrl = formatUrl(url);

      try {
        const token = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

        const reqConfig = { responseType: 'blob' };
        if (!finalUrl.includes('token=')) {
          reqConfig.headers = token ? { Authorization: `Bearer ${token}` } : {};
        }

        const response = await axios.get(finalUrl, reqConfig);

        if (isMounted) {
          setPreviewBlobUrl(URL.createObjectURL(response.data));
        }
      } catch (err) {
        console.error("Failed to load preview blob:", err);
        if (isMounted) {
          setPreviewBlobUrl(finalUrl); // fallback to formatted URL
        }
      } finally {
        if (isMounted) {
          setIsLoadingPreview(false);
        }
      }
    };

    if (isPreviewModalOpen) {
      fetchPreviewImage();
    }

    return () => { isMounted = false; };
  }, [currentPreviewIndex, isPreviewModalOpen, previewImages]);

  const handleOpenPreviewModal = (images, index = 0) => {
    setPreviewImages(images);
    setCurrentPreviewIndex(index);
    setIsPreviewModalOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewImages([]);
    setCurrentPreviewIndex(0);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  };

  useEffect(() => {
    if (user) {
      setCurrentUserName(user.name || user.username || "System");
    }
  }, [user]);

  const handlePrevPreview = () => {
    setCurrentPreviewIndex((prev) => (prev > 0 ? prev - 1 : previewImages.length - 1));
  };

  const handleNextPreview = () => {
    setCurrentPreviewIndex((prev) => (prev < previewImages.length - 1 ? prev + 1 : 0));
  };

  // ======== Image Preview Slider Modal ========
  const renderPreviewModal = () => (
    <Dialog
      open={isPreviewModalOpen}
      onClose={handleClosePreviewModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          width: { xs: "95vw", md: "100%" },
          margin: { xs: 2, md: 4 }
        }
      }}
    >
      <DialogContent sx={{ p: { xs: 1, md: 2 }, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '40vh', md: '60vh' } }}>
        <MDButton
          onClick={handleClosePreviewModal}
          sx={{ position: 'absolute', top: 12, right: 12, color: '#344767', zIndex: 10, minWidth: { xs: 32, md: 40 }, width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 }, borderRadius: "50%", padding: 0, backgroundColor: 'rgba(52, 71, 103, 0.05)', '&:hover': { backgroundColor: 'rgba(52, 71, 103, 0.1)' } }}
        >
          <span className="material-icons" style={{ fontSize: "20px" }}>close</span>
        </MDButton>

        {previewImages.length > 1 && (
          <>
            <MDButton
              onClick={handlePrevPreview}
              sx={{ position: 'absolute', left: 12, color: '#344767', zIndex: 10, minWidth: { xs: 32, md: 40 }, width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 }, borderRadius: "50%", padding: 0, backgroundColor: 'rgba(52, 71, 103, 0.05)', '&:hover': { backgroundColor: 'rgba(52, 71, 103, 0.1)' } }}
            >
              <span className="material-icons" style={{ fontSize: "24px" }}>chevron_left</span>
            </MDButton>
            <MDButton
              onClick={handleNextPreview}
              sx={{ position: 'absolute', right: 12, color: '#344767', zIndex: 10, minWidth: { xs: 32, md: 40 }, width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 }, borderRadius: "50%", padding: 0, backgroundColor: 'rgba(52, 71, 103, 0.05)', '&:hover': { backgroundColor: 'rgba(52, 71, 103, 0.1)' } }}
            >
              <span className="material-icons" style={{ fontSize: "24px" }}>chevron_right</span>
            </MDButton>
          </>
        )}

        {isLoadingPreview ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height={{ xs: "40vh", md: "60vh" }} width="100%">
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <MDBox
            component="img"
            src={previewBlobUrl || previewImages[currentPreviewIndex]}
            key={currentPreviewIndex}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '60vh', md: '70vh' },
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        )}

        {previewImages.length > 1 && (
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{ position: 'absolute', bottom: 16, color: '#344767', backgroundColor: 'rgba(255,255,255,0.9)', px: 2, py: 0.5, borderRadius: '20px', border: "1px solid #e0e0e0" }}
          >
            {currentPreviewIndex + 1} / {previewImages.length}
          </MDTypography>
        )}
      </DialogContent>
    </Dialog>
  );

  const getPakistanTime = () => {
    const date = new Date();
    const options = {
      timeZone: 'Asia/Karachi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    const formatter = new Intl.DateTimeFormat('en-GB', options);
    // e.g. "2026-03-07 13:52"
    const parts = formatter.formatToParts(date);

    let d = '', m = '', y = '', h = '', min = '';
    parts.forEach(p => {
      if (p.type === 'day') d = p.value;
      if (p.type === 'month') m = p.value;
      if (p.type === 'year') y = p.value;
      if (p.type === 'hour') h = p.value;
      if (p.type === 'minute') min = p.value;
    });

    return `${y}-${m}-${d} ${h}:${min}`;
  };

  useEffect(() => {
    const fetchApartments = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.ROOM_DETAILS_TEABLE_DATA, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const result = await response.json();
        if (result.success && result.data) {
          const names = result.data.map(record => record.fields['Listing Name'] || record.fields['Apartment Name ']).filter(Boolean);
          const uniqueNames = [...new Set(names)].sort();
          setApartments(uniqueNames);
        }
      } catch (error) {
        console.error('Error fetching apartments:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const TEABLE_INVENTORY_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_TABLE_ID || GLOBAL_CATEGORY_TABLE_ID;
        const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

        let allRecords = [];
        let hasMore = true;
        let skip = 0;

        while (hasMore) {
          const response = await teableAPI.getRecords(TEABLE_INVENTORY_TABLE_ID, teableToken, { skip });
          const pageRecords = response.data?.records || [];
          const cleanedPage = (pageRecords || []).map(r => ({
            ...r,
            id: teableAPI.sanitize(r.id)
          }));
          if (cleanedPage.length === 0) {
            hasMore = false;
          } else {
            allRecords = allRecords.concat(cleanedPage);
            skip += pageRecords.length;
            if (pageRecords.length < 1000) hasMore = false;
          }
        }

        const catNames = allRecords.map(rec => rec.fields["Name"]).filter(Boolean);
        const uniqueCats = [...new Set(catNames)].sort();
        setCategories(uniqueCats);
        setCategoryRecords(allRecords);

        const existingIds = allRecords.map(rec => {
          const idValue = rec.fields["id "] || rec.fields["id"];
          const num = Number(idValue);
          return (idValue !== null && idValue !== undefined && !isNaN(num) && isFinite(num)) ? num : null;
        }).filter(n => n !== null);

        if (existingIds.length > 0) {
          setNextCategoryId(Math.max(...existingIds) + 1);
        } else {
          setNextCategoryId(1);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        if (error.response) {
          console.error("Teable Error Details:", {
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url
          });
        }
      }
    };

    const fetchItems = async () => {
      setLoading(true);
      console.log("[fetchItems] Starting fetch...");
      try {
        const TEABLE_ITEMS_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || GLOBAL_ITEMS_TABLE_ID;
        const TEABLE_INVENTORY_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_TABLE_ID || GLOBAL_CATEGORY_TABLE_ID;
        const HISTORY_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
        const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

        console.log("[fetchItems] Config:", { TEABLE_ITEMS_TABLE_ID, HISTORY_TABLE_ID, hasToken: !!teableToken });

        let allRecords = [];
        let hasMore = true;
        let skip = 0;

        while (hasMore) {
          const response = await teableAPI.getRecords(TEABLE_ITEMS_TABLE_ID, teableToken, { skip });
          const pageRecords = (response.data?.records || []).map(r => ({
            ...r,
            id: teableAPI.sanitize(r.id)
          }));
          if (pageRecords.length === 0) {
            hasMore = false;
          } else {
            allRecords = allRecords.concat(pageRecords);
            skip += pageRecords.length;
            if (pageRecords.length < 1000) hasMore = false;
          }
        }

        console.log(`[fetchItems] Fetched ${allRecords.length} total items.`);

        const existingIds = allRecords.map(rec => {
          const idValue = rec.fields["id"] || rec.fields["id "];
          const num = Number(idValue);
          return (idValue !== null && idValue !== undefined && !isNaN(num) && isFinite(num)) ? num : null;
        }).filter(n => n !== null);

        if (existingIds.length > 0) {
          setNextItemId(Math.max(...existingIds) + 1);
        } else {
          setNextItemId(1);
        }

        let allHistory = [];
        const fetchHistoryFromTable = async (tableId) => {
          const cleanTableId = teableAPI.sanitize(tableId);
          console.log(`[fetchItems] Fetching history from ${cleanTableId}...`);
          let hRecords = [];
          let hHasMore = true;
          let hSkip = 0;
          while (hHasMore) {
            try {
              const hRes = await axios.get(
                `${teableAPI.baseUrl()}/api/table/${cleanTableId}/record`,
                {
                  params: { take: 1000, skip: hSkip },
                  headers: { Authorization: `Bearer ${teableToken?.trim()}` }
                }
              );
              const hPage = (hRes.data?.records || []).map(r => ({
                ...r,
                id: teableAPI.sanitize(r.id)
              }));
              if (hPage.length === 0) {
                hHasMore = false;
              } else {
                hRecords = hRecords.concat(hPage);
                hSkip += hPage.length;
                if (hPage.length < 1000) hHasMore = false;
              }
            } catch (e) {
              console.warn(`Error fetching history from ${cleanTableId}:`, e);
              hHasMore = false;
            }
          }
          console.log(`[fetchItems] Fetched ${hRecords.length} history records from ${cleanTableId}.`);
          return hRecords.map(r => ({ ...r, sourceTable: cleanTableId }));
        };

        // Fetch from primary history table
        const primaryHistory = await fetchHistoryFromTable(HISTORY_TABLE_ID);
        allHistory = [...primaryHistory];

        // Fetch from legacy inventory table (if different)
        if (TEABLE_INVENTORY_TABLE_ID && TEABLE_INVENTORY_TABLE_ID !== HISTORY_TABLE_ID && TEABLE_INVENTORY_TABLE_ID !== TEABLE_ITEMS_TABLE_ID) {
          const legacyHistory = await fetchHistoryFromTable(TEABLE_INVENTORY_TABLE_ID);
          allHistory = [...allHistory, ...legacyHistory];
        }

        console.log(`[fetchItems] Total history records to process: ${allHistory.length}`);

        // IMPORTANT: Add items themselves as the first history record (as requested)
        // BUT only if they don't already have an "Item added" record in the history table
        const itemInitialHistory = allRecords.filter(rec => {
          const rf = rec.fields;
          const rProp = (rf["Listing Name"] || rf["Apartment Name "] || "").toString().toLowerCase().trim();
          const rItem = (rf["Item Name"] || rf["Item"] || "").toString().toLowerCase().trim();

          return !allHistory.some(h => {
            const hf = h.fields;
            const hProp = (hf["Listing Name"] || hf["Apartment Name "] || "").toString().toLowerCase().trim();
            const hItem = (hf["Item Name"] || hf["Item"] || hf["Name"] || "").toString().toLowerCase().trim();
            const hDesc = (hf["Description"] || "").toString().toLowerCase().trim();

            return hProp === rProp && hItem === rItem && hDesc.includes("added to inventory");
          });
        }).map(rec => ({
          ...rec,
          isInitial: true, // Flag as synthesized initial record
          fields: {
            ...rec.fields,
            "Recorded at": rec.fields["Created at"] || rec.fields["Recorded at"],
            "Item Status": rec.fields["Item Status"] || "Good",
            "Quantity": rec.fields["Quantity"],
            "Price": rec.fields["Cost"] || rec.fields["Cost "] || rec.fields["Price"],
            "Description": "New Item added to inventory",
            "Name": "System"
          }
        }));
        allHistory = [...allHistory, ...itemInitialHistory];

        // Remove exact duplicates (same property, item, status, and time)
        const uniqueHistory = [];
        const seen = new Set();
        allHistory.forEach(h => {
          const f = h.fields;
          const p = (f["Listing Name"] || f["Apartment Name "] || "").toString().toLowerCase().trim();
          const i = (f["Item Name"] || f["Item"] || "").toString().toLowerCase().trim();
          const s = (f["Item Status"] || "").toString().toLowerCase().trim();
          const t = new Date(f["Recorded at"] || f["Created at"] || 0).getTime();
          // Using minute precision to handle slight differences in server vs client timestamps
          const tMinute = Math.floor(t / 60000);
          const key = `${p}|${i}|${s}|${tMinute}`;
          if (!seen.has(key)) {
            uniqueHistory.push(h);
            seen.add(key);
          }
        });
        allHistory = uniqueHistory;

        const damagedItemsMap = {};
        // Sort history by 'Recorded at' or similar if available, or process in reverse to get LATEST status
        const sortedHistory = [...allHistory].sort((a, b) => {
          const dateA = new Date(a.fields["Recorded at"] || a.fields["Created at"] || 0);
          const dateB = new Date(b.fields["Recorded at"] || b.fields["Created at"] || 0);
          return dateB - dateA; // Latest first
        });

        sortedHistory.forEach(record => {
          const status = record.fields["Item Status"];
          const f = record.fields;

          const getVal = (v) => {
            if (v === null || v === undefined) return "";
            if (Array.isArray(v)) {
              const first = v[0];
              if (typeof first === 'object' && first !== null) return first.value || first.name || first.id || "";
              return first || "";
            }
            if (typeof v === 'object') return v.value || v.name || v.id || "";
            return v;
          };

          const propertyName = (getVal(f["Listing Name"]) || getVal(f["Apartment Name "]) || getVal(f["Apartment Name"]) || getVal(f["Property Name"]) || getVal(f["Property"]) || getVal(f["Apartment"]) || getVal(f["Listing"]) || getVal(f["Unit Number"]) || "").toString().trim().toLowerCase();
          const itemName = (getVal(f["Item Name"]) || getVal(f["Item Name "]) || getVal(f["Item"]) || getVal(f["Name"]) || getVal(f["Item Listing"]) || "").toString().trim().toLowerCase();
          const key = `${propertyName}-${itemName}`;

          if (!damagedItemsMap[key]) {
            damagedItemsMap[key] = { status, historyRecord: record };
          }
        });

        const syncedItems = allRecords.map(item => {
          const itemId = item.id;
          const f = item.fields;

          // 1. Try matching by internal Record ID (for Link Fields)
          const historyByLink = sortedHistory.find(h => {
            return Object.values(h.fields).some(val =>
              Array.isArray(val) ? val.includes(itemId) : val === itemId
            );
          });

          if (historyByLink) {
            return { ...item, fields: { ...item.fields, "Item Status": historyByLink.fields["Item Status"] } };
          }

          // 2. Fallback to Name-based matching
          const getVal = (v) => {
            if (v === null || v === undefined) return "";
            if (Array.isArray(v)) {
              const first = v[0];
              if (typeof first === 'object' && first !== null) return first.value || first.name || first.id || "";
              return first || "";
            }
            if (typeof v === 'object') return v.value || v.name || v.id || "";
            return v;
          };
          const propertyName = (getVal(f["Listing Name"]) || getVal(f["Apartment Name "]) || getVal(f["Apartment Name"]) || getVal(f["Property Name"]) || getVal(f["Property"]) || getVal(f["Apartment"]) || getVal(f["Listing"]) || getVal(f["Unit Number"]) || "").toString().trim().toLowerCase();
          const itemName = (getVal(f["Item Name"]) || getVal(f["Item Name "]) || getVal(f["Item"]) || getVal(f["Name"]) || getVal(f["Item Listing"]) || "").toString().trim().toLowerCase();
          const key = `${propertyName}-${itemName}`;
          // 2. Try matching by Custom Numeric ID
          const customId = f["id"] || f["id "];
          const historyByCustomId = sortedHistory.find(h => {
            const hf = h.fields;
            const hCustomId = hf["Item ID"] || hf["item_id"] || hf["Item ID "];
            return customId && hCustomId && String(customId) === String(hCustomId);
          });

          if (historyByCustomId) {
            return { ...item, fields: { ...item.fields, "Item Status": historyByCustomId.fields["Item Status"] } };
          }

          // 3. Fallback to Name-based matching
          const historyEntry = damagedItemsMap[key];

          if (historyEntry) {
            return { ...item, fields: { ...item.fields, "Item Status": historyEntry.status } };
          }

          // If no history found, keep original status but default to "Good" if it was one of the tracked statuses
          const currentStatus = item.fields["Item Status"];
          if (currentStatus === "Damaged" || currentStatus === "Misplaced" || currentStatus === "In Repair") {
            return { ...item, fields: { ...item.fields, "Item Status": "Good" } };
          }
          return item;
        });

        setItems(syncedItems);
        setHistoryRecords(allHistory);

        if (allHistory.length > 0) {
          const validIds = allHistory.map(h => {
            const idValue = h.fields["id"];
            const num = Number(idValue);
            return (idValue !== null && idValue !== undefined && !isNaN(num) && isFinite(num)) ? num : null;
          }).filter(n => n !== null);

          if (validIds.length > 0) {
            setNextHistoryId(Math.max(...validIds) + 1);
          } else {
            setNextHistoryId(1);
          }
        } else {
          setNextHistoryId(1);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApartments();
    fetchCategories();
    fetchItems();
  }, []);

  const handleApartmentChange = (event) => {
    setSelectedApartment(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // ======== Modal Handlers ========
  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setNewCategoryName("");
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim()) {
      showMessage("Name cannot be empty", "error");
      return;
    }

    // ✅ Duplicate category check
    const isDuplicateCategory = categories.some(
      c => c.trim().toLowerCase() === newCategoryName.trim().toLowerCase()
    );
    if (isDuplicateCategory) {
      showMessage(`Category "${newCategoryName.trim()}" already exists.`, "error");
      return;
    }

    // Declare outside try so catch block can access them too
    const TEABLE_BASE_URL = process.env.REACT_APP_TEABLE_BASE_URL || "https://teable.namuve.com";
    const TEABLE_INVENTORY_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_TABLE_ID || GLOBAL_CATEGORY_TABLE_ID;
    const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

    try {
      const recordPayload = {
        records: [{
          fields: {
            "id ": nextCategoryId,
            "Name": newCategoryName.trim(),
            "Created at": getPakistanTime()
          }
        }]
      };

      await axios.post(
        `${TEABLE_BASE_URL}/api/table/${TEABLE_INVENTORY_TABLE_ID}/record`,
        recordPayload,
        {
          headers: {
            'Authorization': `Bearer ${teableToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("Saved new category...", { id: nextCategoryId, name: newCategoryName, createdAt: getPakistanTime() });

      setCategories(prev => [...prev, newCategoryName.trim()].sort());
      setNextCategoryId(prev => prev + 1);
      handleCloseCategoryModal();
      window.location.reload();
    } catch (error) {
      console.error("Error saving category:", error.response?.data || error);

      // In case user literally meant 'id' instead of 'id '
      if (error.response?.data?.message?.includes("id ")) {
        try {
          const retryPayload = {
            records: [{
              fields: {
                "id": nextCategoryId,
                "Name": newCategoryName.trim(),
                "Created at": getPakistanTime()
              }
            }]
          };
          await axios.post(
            `${TEABLE_BASE_URL}/api/table/${TEABLE_INVENTORY_TABLE_ID}/record`,
            retryPayload,
            {
              headers: {
                'Authorization': `Bearer ${teableToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          setCategories(prev => [...prev, newCategoryName.trim()].sort());
          setNextCategoryId(prev => prev + 1);
          handleCloseCategoryModal();
          window.location.reload();
          return;
        } catch (retryError) {
          console.error("Retry failed:", retryError.response?.data || retryError);
        }
      }

      showMessage("Failed to save category in database. Check console for details.", "error");
    }
  };

  const handleOpenManageCategoriesModal = () => setIsManageCategoriesModalOpen(true);
  const handleCloseManageCategoriesModal = () => setIsManageCategoriesModalOpen(false);

  const handleOpenEditCategoryModal = (record) => {
    setSelectedCategoryForEdit(record);
    setEditCategoryName(record.fields["Name"] || "");
    setIsEditCategoryModalOpen(true);
  };

  const handleCloseEditCategoryModal = () => {
    setIsEditCategoryModalOpen(false);
    setSelectedCategoryForEdit(null);
    setEditCategoryName("");
  };

  const handleSaveEditCategory = async () => {
    if (!selectedCategoryForEdit || !editCategoryName.trim()) return;
    setIsSubmittingEditCategory(true);
    try {
      const TEABLE_BASE_URL = process.env.REACT_APP_TEABLE_BASE_URL || "https://teable.namuve.com";
      const TEABLE_INVENTORY_TABLE_ID = process.env.REACT_APP_TEABLE_INVENTORY_TABLE_ID || "tbloGWTlkHhGzMmH0oH";
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;

      // Correct Teable format: { record: { fields: { ... } } }
      const payload = {
        record: {
          fields: { "Name": editCategoryName.trim() }
        }
      };

      const cleanCatId = String(selectedCategoryForEdit.id || "").split(':')[0].trim();
      console.log("DEBUG Category Update - URL:", `${TEABLE_BASE_URL}/api/table/${TEABLE_INVENTORY_TABLE_ID}/record`);
      console.log("DEBUG Category Update - Payload:", JSON.stringify({ records: [{ id: cleanCatId, fields: { "Name": editCategoryName.trim() } }] }));

      await axios.patch(
        `${TEABLE_BASE_URL}/api/table/${TEABLE_INVENTORY_TABLE_ID}/record`,
        { records: [{ id: cleanCatId, fields: { "Name": editCategoryName.trim() } }] },
        {
          headers: {
            'Authorization': `Bearer ${teableToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      const oldName = selectedCategoryForEdit.fields["Name"];
      const newName = editCategoryName.trim();

      setCategories(prev => prev.map(c => c === oldName ? newName : c).sort());
      setCategoryRecords(prev => prev.map(r =>
        String(r.id || "").split(':')[0].trim() === cleanCatId ? { ...r, fields: { ...r.fields, "Name": newName } } : r
      ));

      // Also need to update items that use this category
      setItems(prev => prev.map(item =>
        item.fields["Category"] === oldName ? { ...item, fields: { ...item.fields, "Category": newName } } : item
      ));

      showMessage("Category updated successfully!", "success");
      handleCloseEditCategoryModal();
    } catch (error) {
      console.error("CRITICAL ERROR UPDATING CATEGORY:", error);
      const serverMsg = error.response?.data?.message || error.response?.data?.error || "Unknown server error";
      console.error("SERVER DETAILS:", serverMsg);

      showMessage(`Failed: ${serverMsg}`, "error");
    } finally {
      setIsSubmittingEditCategory(false);
    }
  };

  const handleOpenItemModal = () => {
    setIsItemModalOpen(true);
  };

  const handleCloseItemModal = () => {
    setIsItemModalOpen(false);
    setNewItemListingName("");
    setNewItemName("");
    setNewItemCategory("");
    setNewItemQuantity("");
    setNewItemCost("");
    setNewItemAttachments([]);
    setNewItemStatus("Good");
  };

  // ======== History Modal Handlers ========
  const handleOpenHistoryModal = (item) => {
    setSelectedItemForHistory(item);
    const itemId = item.id;
    const ifields = item.fields;

    const getVal = (v) => {
      if (v === null || v === undefined) return "";
      if (Array.isArray(v)) {
        const first = v[0];
        if (typeof first === 'object' && first !== null) return first.value || first.name || first.id || "";
        return first || "";
      }
      if (typeof v === 'object') return v.value || v.name || v.id || "";
      return v;
    };

    const itemProperty = (getVal(ifields["Listing Name"]) || getVal(ifields["Apartment Name "]) || getVal(ifields["Apartment Name"]) || getVal(ifields["Property Name"]) || getVal(ifields["Property"]) || getVal(ifields["Apartment"]) || getVal(ifields["Listing"]) || "").toString().trim().toLowerCase();
    const itemName = (getVal(ifields["Item Name"]) || getVal(ifields["Item Name "]) || getVal(ifields["Item"]) || getVal(ifields["Name"]) || getVal(ifields["Item Listing"]) || "").toString().trim().toLowerCase();
    const customId = ifields["id"] || ifields["id "];

    console.log("Opening history for ID:", itemId, "Name:", itemName, "Custom ID:", customId);

    const filtered = historyRecords.filter(h => {
      const hfields = h.fields;

      // 1. Match by Record ID (Link Field)
      const isLinkedById = Object.values(hfields).some(val =>
        Array.isArray(val) ? val.includes(itemId) : val === itemId
      );
      if (isLinkedById) return true;

      // 2. Match by Custom Numeric ID
      const hCustomId = hfields["Item ID"] || hfields["item_id"] || hfields["Item ID "];
      if (customId && hCustomId && String(customId) === String(hCustomId)) return true;

      // 3. Match by Name (Fallback)
      const hProperty = (getVal(hfields["Listing Name"]) || getVal(hfields["Apartment Name "]) || getVal(hfields["Apartment Name"]) || getVal(hfields["Property Name"]) || getVal(hfields["Property"]) || getVal(hfields["Apartment"]) || getVal(hfields["Listing"]) || "").toString().trim().toLowerCase();
      const hName = (getVal(hfields["Item Name"]) || getVal(hfields["Item Name "]) || getVal(hfields["Item"]) || getVal(hfields["Name"]) || getVal(hfields["Item Listing"]) || "").toString().trim().toLowerCase();

      // Ensure we don't match empty strings
      if (!itemName || !itemProperty || !hName || !hProperty) return false;

      return hProperty === itemProperty && hName === itemName;
    }).sort((a, b) => {
      const dateA = a.fields["Recorded at"] ? new Date(a.fields["Recorded at"]) : new Date(0);
      const dateB = b.fields["Recorded at"] ? new Date(b.fields["Recorded at"]) : new Date(0);
      return dateB - dateA;
    });

    console.log("Filtered history records:", filtered);

    setItemHistoryRecords(filtered);
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedItemForHistory(null);
    setItemHistoryRecords([]);
  };

  const handleOpenEditHistoryModal = (record) => {
    setSelectedRecordForEdit(record);
    setEditHistoryStatus(record.fields["Item Status"] || "");
    setEditHistoryDescription(record.fields["Description"] || "");
    setEditHistoryPrice(record.fields["Price"] || "");
    setEditHistorySendTo(record.fields["Send to"] || "");
    setEditHistoryReservationId(record.fields["Reservation ID"] ? String(record.fields["Reservation ID"]) : "");
    setEditHistoryAttachments(record.fields["Attachments"] || []);
    setEditHistoryNewFiles([]);
    setIsEditHistoryModalOpen(true);
    // Fetch reservations for the listing associated with this history record
    const listingName = record.fields["Listing Name"] || record.fields["listingName"] || "";
    if (listingName) fetchReservationsForListing(listingName);
  };

  const handleCloseEditHistoryModal = () => {
    setIsEditHistoryModalOpen(false);
    setEditHistoryError(null);
    setSelectedRecordForEdit(null);
    setEditHistoryStatus("");
    setEditHistoryDescription("");
    setEditHistoryPrice("");
    setEditHistorySendTo("");
    setEditHistoryReservationId("");
    setEditHistoryAttachments([]);
    setEditHistoryNewFiles([]);
  };

  const handleEditHistoryFileChange = (e) => {
    if (e.target.files) {
      const incoming = Array.from(e.target.files);
      setEditHistoryNewFiles(prev => {
        const existingNames = new Set([
          ...prev.map(f => f.name.toLowerCase()),
          ...(editHistoryAttachments || []).map(a => (a.name || "").toLowerCase())
        ]);
        const unique = incoming.filter(f => {
          if (existingNames.has(f.name.toLowerCase())) {
            showMessage(`"${f.name}" is already attached.`, "error");
            return false;
          }
          return true;
        });
        return [...prev, ...unique];
      });
    }
  };

  const handleRemoveEditHistoryExistingAttachment = (attachmentId) => {
    setEditHistoryAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleRemoveEditHistoryNewFile = (index) => {
    setEditHistoryNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEditHistory = async () => {
    if (!selectedRecordForEdit) return;
    if (selectedRecordForEdit.isInitial) {
      showMessage("Synthesized 'Item Added' records cannot be edited.", "warning");
      return;
    }

    setIsSubmittingEditHistory(true);
    setEditHistoryError(null);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const tableId = selectedRecordForEdit.sourceTable || process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const recordId = teableAPI.sanitize(selectedRecordForEdit.id);
      if (!recordId) {
        setEditHistoryError("Invalid Record ID. Cannot save changes.");
        return;
      }

      // 1. Prepare fields with clean IDs
      const ACTIVE_HIDDEN_ID = await discoverTableFieldId(tableId);

      // Use field IDs as keys (matching fieldKeyType=id in the PATCH URL)
      // Field IDs confirmed via full_schema_scan.js on History Table (tbllxFsIqInCDhYIgPK)
      const updatedFields = {
        "Item Status": editHistoryStatus,
        "Description": editHistoryDescription || "",
        "Price": (editHistoryPrice !== "" && editHistoryPrice !== null && !isNaN(Number(editHistoryPrice))) ? Number(editHistoryPrice) : null,
        "Reservation ID": (editHistoryReservationId !== "" && editHistoryReservationId !== null && !isNaN(Number(editHistoryReservationId))) ? Number(editHistoryReservationId) : null,
        "Send to": editHistorySendTo || null
      };

      if (editHistoryReservationId !== "" && editHistoryReservationId !== null) {
        const selectedRes = recentReservations.find(r => String(r.fields["Reservation ID"]) === String(editHistoryReservationId));
        if (selectedRes && selectedRes.fields["Guest Name"]) {
          updatedFields["fldepZrWyPcuoF3ypxr"] = selectedRes.fields["Guest Name"]; // Name (Guest Name)
        }
      }

      // Restore REQUIRED Teable schema validators for existing attachments
      const existingAttachments = (editHistoryAttachments || [])
        .filter(a => a && a.id)
        .map(a => ({
          id: teableAPI.sanitize(a.id),
          name: a.name || "attachment.png",
          path: a.path || `private/table/${tableId}/${teableAPI.sanitize(a.id)}`,
          token: a.token || teableAPI.sanitize(a.id),
          size: a.size || 1024,
          mimetype: a.mimetype || "image/png"
        }));

      // Use field name for existing attachments in PATCH (fieldKeyType=id not supported on PATCH endpoint)
      if (existingAttachments.length > 0) {
        updatedFields["Attachments"] = existingAttachments;
      }
      // Do NOT send null for attachments — omit if empty, uploadAttachment handles new files

      console.log("[Edit History] PATCHing Table:", tableId, "Record:", recordId);

      // 2. Perform BULK Patch (standard field names)
      await teableAPI.patchRecords(tableId, [{ id: recordId, fields: updatedFields }], teableToken);

      // 3. Upload new files - use verified ID (With protective backend delay to bypass 500 Lock/Race Conditions)
      if (editHistoryNewFiles.length > 0) {
        for (const file of editHistoryNewFiles) {
          await teableAPI.uploadAttachment(tableId, recordId, ACTIVE_HIDDEN_ID, file, teableToken);
          await new Promise(resolve => setTimeout(resolve, 800)); // 800ms Buffer Space
        }
      }

      // 4. Refetch finalized record
      const res = await teableAPI.getRecord(tableId, recordId, teableToken);
      const finalFields = res.data.fields;

      // 5. Update local state
      const updateState = (prev) => prev.map(h => teableAPI.sanitize(h.id) === recordId ? { ...h, fields: { ...finalFields, Attachments: finalFields[ACTIVE_HIDDEN_ID] || finalFields["Attachments"] } } : h);
      setHistoryRecords(updateState);
      setItemHistoryRecords(updateState);

      showMessage("Change history updated successfully!", "success");
      handleCloseEditHistoryModal();
    } catch (error) {
      console.error("REWRITE ERROR [Edit History]:", error.response?.data || error);
      const errorData = error.response?.data;
      const detailedMsg = errorData ? JSON.stringify(errorData, null, 2) : error.message;
      setEditHistoryError(detailedMsg);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingEditHistory(false);
    }
  };


  const handleOpenItemEditModal = (item) => {
    setSelectedItemForEdit(item);
    setEditItemListingName(item.fields["Listing Name"] || item.fields["Apartment Name "] || "");
    setEditItemName(item.fields["Item Name"] || "");
    setEditItemCategory(item.fields["Category"] || "");
    setEditItemQuantity(item.fields["Quantity"] || "");
    setEditItemCost(item.fields["Cost"] || item.fields["Cost "] || "");

    // Get attachments based on both possible field names/IDs
    const attachments = item.fields["Attachments"] || item.fields["fldFtkiw4EHV8F2mmtc"] || [];
    setEditItemAttachments(attachments);
    setEditItemNewFiles([]);
    setEditItemError(null);
    setIsEditItemModalOpen(true);
  };

  const handleCloseItemEditModal = () => {
    setIsEditItemModalOpen(false);
    setEditItemError(null);
    setSelectedItemForEdit(null);
    setEditItemListingName("");
    setEditItemName("");
    setEditItemCategory("");
    setEditItemQuantity("");
    setEditItemCost("");
    setEditItemAttachments([]);
    setEditItemNewFiles([]);
  };

  const handleEditItemFileChange = (e) => {
    if (e.target.files) {
      const incoming = Array.from(e.target.files);
      setEditItemNewFiles(prev => {
        const existingNames = new Set([
          ...prev.map(f => f.name.toLowerCase()),
          ...(editItemAttachments || []).map(a => (a.name || "").toLowerCase())
        ]);
        const unique = incoming.filter(f => {
          if (existingNames.has(f.name.toLowerCase())) {
            showMessage(`"${f.name}" is already attached.`, "error");
            return false;
          }
          return true;
        });
        return [...prev, ...unique];
      });
    }
  };

  const handleRemoveEditItemExistingAttachment = (attachmentId) => {
    setEditItemAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleRemoveEditItemNewFile = (index) => {
    setEditItemNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const [tableFieldMap, setTableFieldMap] = useState({
    // Confirmed via full_schema_scan.js (scanning 20 records)
    "tbl5L6sBe405YR3spyc": "fldFLy3yARoltR12LSc",   // Items Table → Attachments
    "tbllxFsIqInCDhYIgPK": "fldnielwPiUjUDrp1ul"    // History Table → Attachments
  });

  let HISTORY_ATTACH_ID; // Scoped for component-wide reporting
  // Universal Discovery: Identify the correct Attachment Field ID for ANY table
  const discoverTableFieldId = async (tableId) => {
    if (tableFieldMap[tableId]) return tableFieldMap[tableId];
    const teableToken = teableAPI.getToken();
    
    try {
      // FALLBACK: Scan a record for array-of-objects that look like attachments (have .path or .mimetype)
      const recUrl = `${teableAPI.baseUrl()}/api/table/${tableId}/record?take=100&fieldKeyType=id`;
      const recRes = await axios.get(recUrl, { headers: { 'Authorization': `Bearer ${teableToken.trim()}` } });
      if (recRes.data.records && recRes.data.records.length > 0) {
        let fid = null;
        for (const record of recRes.data.records) {
          fid = Object.keys(record.fields).find(k => {
            const v = record.fields[k];
            return Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && (v[0].id || v[0].token || v[0].path || v[0].mimetype);
          });
          if (fid) break;
        }
        if (fid) {
          console.log(`[DISCOVERY FALLBACK] Table ${tableId} attachment field ID: ${fid}`);
          setTableFieldMap(prev => ({ ...prev, [tableId]: fid }));
          return fid;
        }
      }
    } catch (e2) {
      console.warn(`[DISCOVERY FALLBACK] Record scan failed for ${tableId}.`);
    }
    return "Attachments";
  };

  // Auto-discover IDs for main tables on mount
  useEffect(() => {
    const initDiscovery = async () => {
      const itemsId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";
      const historyId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || "tblZ3RIIlzq5VCWTDJ0";
      await discoverTableFieldId(itemsId);
      await discoverTableFieldId(historyId);
    };
    initDiscovery();
  }, []);

  const handleSaveItemEdit = async () => {
    if (!selectedItemForEdit) return;
    setIsSubmittingEditItem(true);
    setEditItemError(null);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      // Get the specific ID for this table
      const ACTIVE_FIELD_ID = await discoverTableFieldId(itemsTableId);
      const itemId = teableAPI.sanitize(selectedItemForEdit.id);

      // Still find the display Name for the initial PATCH call
      const availableKeys = Object.keys(selectedItemForEdit.fields);
      const CURRENT_FIELD_NAME = availableKeys.find(k => {
        const val = selectedItemForEdit.fields[k];
        return Array.isArray(val) && val.length > 0 && typeof val[0] === 'object';
      }) || availableKeys.find(k => k.toLowerCase().trim() === "attachments") || "Attachments";

      console.log(`[DEBUG Item Edit] PATCHing Name: "${CURRENT_FIELD_NAME}", Uploading ID: "${ACTIVE_FIELD_ID}"`);

      // 2. Prepare regular fields
      const updatedFields = {
        "Item Name": editItemName,
        "Category": editItemCategory,
        "Listing Name": editItemListingName
      };

      if (editItemQuantity !== "" && editItemQuantity !== null) {
        updatedFields["Quantity"] = Number(editItemQuantity);
      }

      if (editItemCost !== "" && editItemCost !== null) {
        updatedFields["Cost"] = Number(editItemCost);
      }

      // 3. Handle attachments if field was found
      // RESTORED: Teable strictly validates presence of name/path/token/size/mimetype on PATCH arrays
      const existingAttachments = (editItemAttachments || [])
        .filter(a => a && a.id)
        .map(a => ({
          id: teableAPI.sanitize(a.id),
          name: a.name || "attachment.png",
          path: a.path || `private/table/${itemsTableId}/${teableAPI.sanitize(a.id)}`,
          token: a.token || teableAPI.sanitize(a.id),
          size: a.size || 1024,
          mimetype: a.mimetype || "image/png"
        }));

      // REQUIREMENT: "Delete specifically then upload" - We must KEEP existing ones while adding new ones
      updatedFields[CURRENT_FIELD_NAME] = existingAttachments.length > 0 ? existingAttachments : null;

      console.log("[DEBUG Item Edit] Record ID:", itemId);
      console.log("[DEBUG Item Edit] Fields to update:", JSON.stringify(updatedFields, null, 2));

      // 4. Bulk PATCH update
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: updatedFields }], teableToken);

      // 5. Upload new attachments if any (With Delay)
      if (editItemNewFiles.length > 0) {
        console.log(`[DEBUG Item Edit] Uploading to verified ID: "${ACTIVE_FIELD_ID}"`);
        for (const file of editItemNewFiles) {
          await teableAPI.uploadAttachment(itemsTableId, itemId, ACTIVE_FIELD_ID, file, teableToken);
          await new Promise(resolve => setTimeout(resolve, 800)); // Buffer space between uploads
        }
      }

      // 6. Refetch finalized record
      const res = await teableAPI.getRecord(itemsTableId, itemId, teableToken);
      const finalFields = res.data.fields;

      // Update local state
      setItems(prev => prev.map(i =>
        teableAPI.sanitize(i.id) === itemId ? { ...i, fields: finalFields } : i
      ));

      setHistoryRecords(prev => prev.map(h => {
        if (h.isInitial && teableAPI.sanitize(h.id) === itemId) {
          return {
            ...h,
            fields: {
              ...h.fields,
              ...finalFields,
              "Recorded at": h.fields["Recorded at"] || finalFields["Created at"],
              "Item Status": h.fields["Item Status"] || finalFields["Item Status"] || "Good",
              "Price": finalFields["Cost"] || finalFields["Cost "] || finalFields["Price"] || h.fields["Price"],
              "Description": h.fields["Description"] || "New Item added to inventory",
              "Name": h.fields["Name"] || "System"
            }
          };
        }
        return h;
      }));

      showMessage("Item updated successfully!", "success");
      handleCloseItemEditModal();
    } catch (error) {
      console.error("DEBUG ERROR [Item Edit]:", error.response?.data || error);
      const errorMsg = error.response?.data?.message || error.message || "Unknown error";
      const detailedMsg = error.response?.data ? JSON.stringify(error.response.data, null, 2) : errorMsg;
      setEditItemError(detailedMsg);
      showMessage(`Update Failed: ${errorMsg}`, "error");
    } finally {
      setIsSubmittingEditItem(false);
    }
  };

  const handleOpenRetireModal = (item) => {
    setSelectedItemForRetire(item);
    setRetireDescription("");
    setIsRetireModalOpen(true);
  };

  const handleCloseRetireModal = () => {
    setIsRetireModalOpen(false);
    setSelectedItemForRetire(null);
    setRetireDescription("");
  };

  const handleSaveRetireReport = async () => {
    if (!selectedItemForRetire) return;
    setIsSubmittingRetire(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForRetire.fields["Listing Name"] || selectedItemForRetire.fields["Apartment Name "],
        "Item Name": selectedItemForRetire.fields["Item Name"],
        "Item Status": "Retired",
        "Description": retireDescription || "Item retired from inventory.",
        "Recorded at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      const itemId = teableAPI.sanitize(selectedItemForRetire.id);
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: { "Item Status": "Retired" } }], teableToken);

      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[ATTACHMENT_FIELD_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? { ...it, fields: { ...it.fields, "Item Status": "Retired" } } : it
      );
      setItems(updatedItems);
      handleCloseRetireModal();
      showMessage("Item retired successfully!", "success");
    } catch (error) {
      console.error("REWRITE ERROR [Retire Report]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingRetire(false);
    }
  };

  const handleOpenFoundModal = (item) => {
    setSelectedItemForFound(item);
    setFoundDescription("");
    setFoundAttachments([]);
    setIsFoundModalOpen(true);
  };

  const handleCloseFoundModal = () => {
    setIsFoundModalOpen(false);
    setSelectedItemForFound(null);
    setFoundDescription("");
    setFoundAttachments([]);
  };

  const handleSaveFoundBack = async () => {
    if (!selectedItemForFound) return;
    setIsSubmittingFound(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      // 1. Create a "Found" history record
      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForFound.fields["Listing Name"] || selectedItemForFound.fields["Apartment Name "],
        "Item Name": selectedItemForFound.fields["Item Name"],
        "Item Status": "Good",
        "Description": foundDescription || "Item found and returned to inventory.",
        "Recorded at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // Upload attachments - Use verified History ID
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      if (foundAttachments && foundAttachments.length > 0) {
        for (const file of foundAttachments) {
          await teableAPI.uploadAttachment(historyTableId, recordId, HISTORY_ATTACH_ID, file, teableToken);
        }
      }

      // 2. Refetch finalized history record to get attachment data
      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      // 3. Update item status back to "Good"
      const itemId = teableAPI.sanitize(selectedItemForFound.id);
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: { "Item Status": "Good" } }], teableToken);

      // Update local state - Re-uses the ID discovered above
      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[HISTORY_ATTACH_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? { ...it, fields: { ...it.fields, "Item Status": "Good" } } : it
      );
      setItems(updatedItems);
      handleCloseFoundModal();
    } catch (error) {
      console.error("REWRITE ERROR [Found Back]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingFound(false);
    }
  };

  const handleOpenReplaceModal = (item) => {
    console.log("Opening Replace Modal for item:", item);
    setSelectedItemForReplace(item);
    setReplacementItemName(""); // Start empty for manual entry
    setReplaceDescription("");
    setReplaceStatus("Good");
    setReplaceAttachments([]);
    setIsReplaceModalOpen(true);
  };

  const handleCloseReplaceModal = () => {
    setIsReplaceModalOpen(false);
    setSelectedItemForReplace(null);
    setReplaceDescription("");
    setReplaceAttachments([]);
  };

  const handleSaveReplaceReport = async () => {
    if (!selectedItemForReplace) return;
    setIsSubmittingReplace(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      // 1. Create a "Replaced" history record
      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForReplace.fields["Listing Name"] || selectedItemForReplace.fields["Apartment Name "],
        "Item Name": selectedItemForReplace.fields["Item Name"],
        "Item Status": "Replaced",
        "Description": `Replaced with: ${replacementItemName || "New Item"}. ${replaceDescription}`,
        "Recorded at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // Upload attachments - Use verified History ID
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      if (replaceAttachments && replaceAttachments.length > 0) {
        for (const file of replaceAttachments) {
          await teableAPI.uploadAttachment(historyTableId, recordId, HISTORY_ATTACH_ID, file, teableToken);
        }
      }

      // 2. Refetch finalized history record to get attachment data
      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      // 3. Update item status and name in main table
      const itemId = teableAPI.sanitize(selectedItemForReplace.id);
      await teableAPI.patchRecords(itemsTableId, [{
        id: itemId,
        fields: {
          "Item Status": replaceStatus,
          "Item Name": replacementItemName || selectedItemForReplace.fields["Item Name"]
        }
      }], teableToken);

      // Update local state
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[HISTORY_ATTACH_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? {
          ...it,
          fields: {
            ...it.fields,
            "Item Status": replaceStatus,
            "Item Name": replacementItemName || selectedItemForReplace.fields["Item Name"]
          }
        } : it
      );
      setItems(updatedItems);
      handleCloseReplaceModal();
    } catch (error) {
      console.error("REWRITE ERROR [Replace Report]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingReplace(false);
    }
  };

  const handleOpenDamageModal = (item) => {
    setSelectedItemForDamage(item);
    setDamageReservationId("");
    setDamageDescription("");
    setDamageAttachments([]);
    setIsDamageModalOpen(true);
    const lName = item.fields["Listing Name"] || item.fields["Apartment Name "];
    console.log("[Damage Modal] Fetching reservations for listing:", JSON.stringify(lName));
    fetchReservationsForListing(lName);
  };

  const handleCloseDamageModal = () => {
    setIsDamageModalOpen(false);
    setSelectedItemForDamage(null);
    setDamageReservationId("");
    setDamageDescription("");
    setDamageAttachments([]);
  };

  const handleCloseRepairModal = () => {
    setIsRepairModalOpen(false);
    setSelectedItemForRepair(null);
    setRepairDescription("");
    setRepairPrice("");
    setRepairSendTo("");
    setRepairAttachments([]);
  };

  const handleOpenRepairModal = (item) => {
    setSelectedItemForRepair(item);
    setRepairDescription("");
    setRepairPrice("");
    setRepairSendTo("");
    setRepairAttachments([]);
    setIsRepairModalOpen(true);
  };

  const handleSaveRepairReport = async () => {
    if (!selectedItemForRepair) return;
    setIsSubmittingRepair(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      // 1. Create a "In Repair" history record
      const pakTime = getPakistanTime();
      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForRepair.fields["Listing Name"] || selectedItemForRepair.fields["Apartment Name "],
        "Item Name": selectedItemForRepair.fields["Item Name"],
        "Item Status": "In Repair",
        "Description": repairDescription || "Item sent for repair.",
        "Send to": repairSendTo,
        "Recorded at": pakTime,
        "Name": user?.name || user?.username || "System"
      };
      if (repairPrice !== "" && !isNaN(Number(repairPrice))) {
        historyFields["Price"] = Number(repairPrice);
      }

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // Upload attachments - Use verified History ID
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      if (repairAttachments && repairAttachments.length > 0) {
        for (const file of repairAttachments) {
          await teableAPI.uploadAttachment(historyTableId, recordId, HISTORY_ATTACH_ID, file, teableToken);
        }
      }

      // 2. Refetch finalized history record to get attachment data
      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      // 3. Update item status in main table
      const itemId = teableAPI.sanitize(selectedItemForRepair.id);
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: { "Item Status": "In Repair" } }], teableToken);

      // Update local state
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[HISTORY_ATTACH_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? { ...it, fields: { ...it.fields, "Item Status": "In Repair" } } : it
      );
      setItems(updatedItems);
      handleCloseRepairModal();
    } catch (error) {
      console.error("REWRITE ERROR [Repair Report]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingRepair(false);
    }
  };

  const handleOpenMisplacedModal = (item) => {
    setSelectedItemForMisplaced(item);
    setMisplacedReservationId("");
    setMisplacedDescription("");
    setMisplacedAttachments([]);
    setIsMisplacedModalOpen(true);
    const lName = item.fields["Listing Name"] || item.fields["Apartment Name "];
    console.log("[Misplaced Modal] Fetching reservations for listing:", JSON.stringify(lName));
    fetchReservationsForListing(lName);
  };

  const handleCloseMisplacedModal = () => {
    setIsMisplacedModalOpen(false);
    setSelectedItemForMisplaced(null);
    setMisplacedDescription("");
    setMisplacedAttachments([]);
  };

  const handleSaveMisplacedReport = async () => {
    if (!selectedItemForMisplaced) return;
    setIsSubmittingMisplaced(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForMisplaced.fields["Listing Name"] || selectedItemForMisplaced.fields["Apartment Name "],
        "Item Name": selectedItemForMisplaced.fields["Item Name"],
        "Item Status": "Misplaced",
        "Description": misplacedDescription,
        "Recorded at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };
      if (misplacedReservationId) {
        historyFields["Reservation ID"] = Number(misplacedReservationId);
        const selectedRes = recentReservations.find(r => String(r.fields["Reservation ID"]) === String(misplacedReservationId));
        if (selectedRes && selectedRes.fields["Guest Name"]) {
          historyFields["Guest Name"] = selectedRes.fields["Guest Name"];
        }
      }

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // Upload attachments - Use verified History ID
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      if (misplacedAttachments && misplacedAttachments.length > 0) {
        for (const file of misplacedAttachments) {
          await teableAPI.uploadAttachment(historyTableId, recordId, HISTORY_ATTACH_ID, file, teableToken);
        }
      }

      // 2. Refetch finalized history record to get attachment data
      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      // 3. Update item status in main table
      const itemId = teableAPI.sanitize(selectedItemForMisplaced.id);
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: { "Item Status": "Misplaced" } }], teableToken);

      // Update local state
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[HISTORY_ATTACH_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? { ...it, fields: { ...it.fields, "Item Status": "Misplaced" } } : it
      );
      setItems(updatedItems);
      handleCloseMisplacedModal();
    } catch (error) {
      console.error("REWRITE ERROR [Misplaced Report]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingMisplaced(false);
    }
  };

  const handleSaveDamageReport = async () => {
    setIsSubmittingDamage(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const historyTableId = process.env.REACT_APP_TEABLE_INVENTORY_HISTORY_TABLE_ID || GLOBAL_HISTORY_TABLE_ID;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      // 1. Create a "Damaged" history record
      const historyFields = {
        "id": nextHistoryId,
        "Listing Name": selectedItemForDamage.fields["Listing Name"],
        "Item Name": selectedItemForDamage.fields["Item Name"],
        "Item Status": "Damaged",
        "Description": damageDescription,
        "Recorded at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };
      if (damageReservationId) {
        historyFields["Reservation ID"] = Number(damageReservationId);
        const selectedRes = recentReservations.find(r => String(r.fields["Reservation ID"]) === String(damageReservationId));
        if (selectedRes && selectedRes.fields["Guest Name"]) {
          historyFields["Guest Name"] = selectedRes.fields["Guest Name"];
        }
      }

      const postRes = await teableAPI.postRecords(historyTableId, [{ fields: historyFields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // Upload attachments - Use verified History ID
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      if (damageAttachments && damageAttachments.length > 0) {
        for (const file of damageAttachments) {
          await teableAPI.uploadAttachment(historyTableId, recordId, HISTORY_ATTACH_ID, file, teableToken);
        }
      }

      // 2. Refetch finalized history record to get attachment data
      const historyRes = await teableAPI.getRecord(historyTableId, recordId, teableToken);
      const finalHistoryFields = historyRes.data.fields;

      // 3. Update item status in main table
      const itemId = teableAPI.sanitize(selectedItemForDamage.id);
      await teableAPI.patchRecords(itemsTableId, [{ id: itemId, fields: { "Item Status": "Damaged" } }], teableToken);

      // Update local state
      HISTORY_ATTACH_ID = await discoverTableFieldId(historyTableId);
      const cleanedHistoryRecord = { ...historyRes.data, fields: { ...finalHistoryFields, Attachments: finalHistoryFields[HISTORY_ATTACH_ID] || finalHistoryFields["Attachments"] }, id: recordId, sourceTable: teableAPI.sanitize(historyTableId) };
      setHistoryRecords(prev => [cleanedHistoryRecord, ...prev]);
      setNextHistoryId(prev => prev + 1);

      const updatedItems = items.map(it =>
        teableAPI.sanitize(it.id) === itemId ? { ...it, fields: { ...it.fields, "Item Status": "Damaged" } } : it
      );
      setItems(updatedItems);

      handleCloseDamageModal();
      showMessage("Damage report saved successfully!", "success");
    } catch (error) {
      console.error("REWRITE ERROR [Damage Report]:", error.response?.data || error);
      showMessage(`Update Failed: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingDamage(false);
    }
  };

  const handleSaveItem = async () => {
    if (!newItemListingName.trim() || !newItemName.trim() || !newItemCategory.trim() || String(newItemQuantity).trim() === "" || !newItemStatus.trim()) {
      showMessage("Please fill in all required fields", "error");
      return;
    }

    // ✅ Duplicate item check (same Item Name + Listing Name)
    const isDuplicateItem = items.some(it =>
      (it.fields["Item Name"] || "").trim().toLowerCase() === newItemName.trim().toLowerCase() &&
      (it.fields["Listing Name"] || it.fields["Apartment Name "] || "").trim().toLowerCase() === newItemListingName.trim().toLowerCase()
    );
    if (isDuplicateItem) {
      showMessage(`Item "${newItemName.trim()}" already exists for "${newItemListingName.trim()}".`, "error");
      return;
    }

    setIsSubmittingItem(true);
    try {
      const teableToken = process.env.REACT_APP_TEABLE_BEARER_TOKEN;
      const itemsTableId = process.env.REACT_APP_TEABLE_INVENTORY_ITEMS_TABLE_ID || "tblgs4Wcu4sfP9gj1c9";

      const fields = {
        "id": nextItemId,
        "Listing Name": newItemListingName,
        "Item Name": newItemName.trim(),
        "Item Status": newItemStatus,
        "Category": newItemCategory,
        "Quantity": Math.max(0, Number(newItemQuantity)),
        "Unit": newItemUnit || "pcs",
        "Created at": getPakistanTime(),
        "Name": user?.name || user?.username || "System"
      };

      if (newItemCost !== "" && !isNaN(Number(newItemCost))) {
        fields["Cost"] = Number(newItemCost);
      }

      // 1. Create record using BULK POST
      const postRes = await teableAPI.postRecords(itemsTableId, [{ fields }], teableToken);
      const recordId = teableAPI.sanitize(postRes.data.records[0].id);

      // 2. Upload attachments
      if (newItemAttachments && newItemAttachments.length > 0) { // Discover correct attachment field for NEW items (Use verified ID)
        const ATTACH_KEY = await discoverTableFieldId(itemsTableId);
        console.log(`[DEBUG Add Item] Uploading to verified ID: ${ATTACH_KEY}`);
        for (const file of newItemAttachments) {
          await teableAPI.uploadAttachment(itemsTableId, recordId, ATTACH_KEY, file, teableToken);
        }
      }

      // 3. Final fetch to get fully finalized record (with photos)
      const finalRes = await teableAPI.getRecord(itemsTableId, recordId, teableToken);
      const finalizedItem = finalRes.data;

      // 4. Update local state
      setItems(prev => [finalizedItem, ...prev]);
      setNextItemId(prev => prev + 1);

      const newHistoryRecord = {
        ...finalizedItem,
        isInitial: true,
        fields: {
          ...finalizedItem.fields,
          "Recorded at": finalizedItem.fields["Created at"],
          "Item Status": finalizedItem.fields["Item Status"] || "Good",
          "Quantity": finalizedItem.fields["Quantity"],
          "Price": finalizedItem.fields["Cost"] || finalizedItem.fields["Price"],
          "Description": "New Item added to inventory",
          "Name": user?.name || user?.username || "System"
        }
      };
      setHistoryRecords(prev => [...prev, newHistoryRecord]);

      showMessage("Item added successfully!", "success");
      handleCloseItemModal();
    } catch (error) {
      console.error("REWRITE ERROR [Add Item]:", error.response?.data || error);
      showMessage(`Failed to save item: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setIsSubmittingItem(false);
    }
  };

  const dropdownSelectSx = {
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #d2d6da",
    backgroundColor: "#ffffff",
    "& .MuiSelect-select": { padding: 0, display: 'flex', alignItems: 'center' },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover": { border: "1px solid #344767" },
    "&:focus-within": { border: "2px solid #344767" },
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    transition: "all 0.2s ease",
  };

  const filteredItems = items.filter(item => {
    const fields = item.fields;

    // Robust value extraction for status
    const getStatusVal = (v) => {
      if (v === null || v === undefined) return "Good";
      if (Array.isArray(v)) {
        const first = v[0];
        if (typeof first === 'object' && first !== null) return first.name || first.value || first.id || "Good";
        return first || "Good";
      }
      if (typeof v === 'object') return v.name || v.value || v.id || "Good";
      return v.toString();
    };

    const rawStatus = getStatusVal(fields["Item Status"]);
    const status = rawStatus.trim();
    const statusLower = status.toLowerCase();
    const selectedStatusLower = (selectedStatus || "").toLowerCase();

    // PERMANENT ARCHIVE: Never show "Retired" items in the frontend list
    if (statusLower === "retired") {
      return false;
    }

    // Property Filter (Matches "Listing Name" from verified scan)
    const itemProperty = fields["Listing Name"] || fields["Apartment Name "] || "";
    const matchesApartment = selectedApartment === "All Properties" || itemProperty === selectedApartment;

    // Category Filter (Matches "Category" from verified scan)
    const matchesCategory = selectedCategory === "All Categories" || fields["Category"] === selectedCategory;

    // Status Filter (Matches "Item Status" from verified scan)
    const matchesStatus = selectedStatus === "All Status" || status === selectedStatus;

    // Search Filter (Matches "Item Name" from verified scan)
    const itemName = fields["Item Name"] || "";
    const category = fields["Category"] || "";
    const matchesSearch = searchTerm === "" ||
      itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesApartment && matchesCategory && matchesStatus && matchesSearch;
  });

  const groupedItems = items.reduce((acc, item) => {
    const propertyName = item.fields["Listing Name"] || item.fields["Apartment Name "] || "Other Items";
    if (!acc[propertyName]) acc[propertyName] = [];
    acc[propertyName].push(item);
    return acc;
  }, {});

  const displayGroupedItems = filteredItems.reduce((acc, item) => {
    const propertyName = item.fields["Listing Name"] || "Other Items";
    if (!acc[propertyName]) acc[propertyName] = [];
    acc[propertyName].push(item);
    return acc;
  }, {});

  // Debugging: Log item counts
  useEffect(() => {
    console.log("Total items loaded:", items.length);
    console.log("Filtered items count:", filteredItems.length);
    console.log("Grouped items (keys):", Object.keys(displayGroupedItems));
  }, [items, filteredItems, displayGroupedItems]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Good': return '#4CAF50';
      case 'Damaged': return '#F44336';
      case 'Misplaced': return '#FF9800';
      case 'In Repair': return '#1A73E8';
      case 'Retired': return '#9E9E9E';
      default: return '#7b809a';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Good': return 'check';
      case 'Damaged': return 'warning_amber';
      case 'Misplaced': return 'help_outline';
      case 'In Repair': return 'build';
      case 'Retired': return 'archive';
      default: return 'info';
    }
  };

  const getHistoryCount = (item) => {
    const itemId = item.id;
    const ifields = item.fields;

    const getVal = (v) => {
      if (v === null || v === undefined) return "";
      if (Array.isArray(v)) {
        const first = v[0];
        if (typeof first === 'object' && first !== null) return first.value || first.name || first.id || "";
        return first || "";
      }
      if (typeof v === 'object') return v.value || v.name || v.id || "";
      return v;
    };

    const itemProperty = (getVal(ifields["Listing Name"]) || getVal(ifields["Apartment Name "]) || getVal(ifields["Apartment Name"]) || getVal(ifields["Property Name"]) || getVal(ifields["Property"]) || getVal(ifields["Apartment"]) || getVal(ifields["Listing"]) || "").toString().trim().toLowerCase();
    const itemName = (getVal(ifields["Item Name"]) || getVal(ifields["Item Name "]) || getVal(ifields["Item"]) || getVal(ifields["Name"]) || getVal(ifields["Item Listing"]) || "").toString().trim().toLowerCase();
    const customId = ifields["id"] || ifields["id "];

    const count = historyRecords.filter(h => {
      const hfields = h.fields;
      const isLinkedById = Object.values(hfields).some(val =>
        Array.isArray(val) ? val.includes(itemId) : val === itemId
      );
      if (isLinkedById) return true;
      const hCustomId = hfields["Item ID"] || hfields["item_id"] || hfields["Item ID "];
      if (customId && hCustomId && String(customId) === String(hCustomId)) return true;
      const hProperty = (getVal(hfields["Listing Name"]) || getVal(hfields["Apartment Name "]) || getVal(hfields["Apartment Name"]) || getVal(hfields["Property Name"]) || getVal(hfields["Property"]) || getVal(hfields["Apartment"]) || getVal(hfields["Listing"]) || "").toString().trim().toLowerCase();
      const hName = (getVal(hfields["Item Name"]) || getVal(hfields["Item Name "]) || getVal(hfields["Item"]) || getVal(hfields["Name"]) || getVal(hfields["Item Listing"]) || "").toString().trim().toLowerCase();
      if (!itemName || !itemProperty || !hName || !hProperty) return false;
      return hProperty === itemProperty && hName === itemName;
    }).length;

    return count;
  };

  const renderRetireButton = (item, isListView = false) => {
    if (!isListView) return null;
    return (
      <Tooltip title="Retire Item" arrow>
        <IconButton
          size="small"
          onClick={!isInventoryViewOnly ? () => handleOpenRetireModal(item) : undefined}
          sx={{
            color: "#7b809a",
            backgroundColor: "#f0f2f5",
            "&:hover": { backgroundColor: "#feebee", color: "#F44336" },
            width: { xs: "auto", md: "26px" },
            height: { xs: "32px", md: "26px" },
            flex: { xs: 1, md: "none" },
            borderRadius: "6px",
            opacity: isInventoryViewOnly ? 0.6 : 1,
            cursor: isInventoryViewOnly ? "not-allowed" : "pointer"
          }}
        >
          <span className="material-icons" style={{ fontSize: "16px" }}>archive</span>
        </IconButton>
      </Tooltip>
    );
  };

  const renderHistoryButton = (item, isListView = false) => {
    const historyCount = getHistoryCount(item);
    if (!isListView) return null; // Only show history button in list view for compactness

    return (
      <Tooltip title="View History" arrow>
        <IconButton
          size="small"
          onClick={() => handleOpenHistoryModal(item)}
          sx={{
            color: "#7b809a",
            backgroundColor: "#f0f2f5",
            "&:hover": { backgroundColor: "#e9ecef" },
            width: { xs: "auto", md: "26px" },
            height: { xs: "32px", md: "26px" },
            flex: { xs: 1, md: "none" },
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.65rem", display: "inline-flex", alignItems: "center" }}>
            {historyCount}
          </MDTypography>
        </IconButton>
      </Tooltip>
    );
  };

  const renderActionButtons = (status, color, item, isListView = false) => {
    const containerSx = {
      display: "flex",
      alignItems: "center",
      gap: { xs: 1, md: 0.5 },
      justifyContent: { xs: "space-between", md: "flex-end" },
      width: { xs: "100%", md: "auto" },
      mt: 0
    };

    const iconBtnSx = (btnColor) => ({
      width: { xs: "auto", md: "26px" },
      height: { xs: "32px", md: "26px" },
      flex: { xs: 1, md: "none" },
      borderRadius: "6px",
      border: `1px solid ${isInventoryViewOnly ? "#d2d6da" : btnColor}`,
      color: isInventoryViewOnly ? "#adb5bd" : btnColor,
      backgroundColor: "transparent",
      "&:hover": { backgroundColor: isInventoryViewOnly ? "transparent" : `${btnColor}10` },
      opacity: isInventoryViewOnly ? 0.6 : 1,
      cursor: isInventoryViewOnly ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    });

    const ActionIcon = ({ icon, onClick, btnColor, tooltip }) => (
      <Tooltip title={tooltip} arrow>
        <IconButton
          size="small"
          onClick={!isInventoryViewOnly ? onClick : undefined}
          sx={iconBtnSx(btnColor)}
        >
          <span className="material-icons" style={{ fontSize: "16px" }}>{icon}</span>
        </IconButton>
      </Tooltip>
    );

    switch (status) {
      case 'Good':
        return (
          <MDBox sx={containerSx}>
            {renderRetireButton(item, isListView)}
            {renderHistoryButton(item, isListView)}
            <ActionIcon icon="edit" onClick={() => handleOpenItemEditModal(item)} btnColor="#7b809a" tooltip="Edit Item" />
            <ActionIcon icon="broken_image" onClick={() => handleOpenDamageModal(item)} btnColor="#F44336" tooltip="Report Damage" />
            <ActionIcon icon="help_outline" onClick={() => handleOpenMisplacedModal(item)} btnColor="#FF9800" tooltip="Report Misplaced" />
          </MDBox>
        );
      case 'Damaged':
        return (
          <MDBox sx={containerSx}>
            {renderRetireButton(item, isListView)}
            {renderHistoryButton(item, isListView)}
            <ActionIcon icon="edit" onClick={() => handleOpenItemEditModal(item)} btnColor="#7b809a" tooltip="Edit Item" />
            <ActionIcon icon="build" onClick={() => handleOpenRepairModal(item)} btnColor="#1A73E8" tooltip="Send to Repair" />
            <ActionIcon icon="swap_horiz" onClick={() => handleOpenReplaceModal(item)} btnColor="#344767" tooltip="Replace Item" />
          </MDBox>
        );
      case 'Misplaced':
        return (
          <MDBox sx={containerSx}>
            {renderRetireButton(item, isListView)}
            {renderHistoryButton(item, isListView)}
            <ActionIcon icon="edit" onClick={() => handleOpenItemEditModal(item)} btnColor="#7b809a" tooltip="Edit Item" />
            <ActionIcon icon="check_circle" onClick={() => handleOpenFoundModal(item)} btnColor="#4CAF50" tooltip="Found - Back OK" />
            <ActionIcon icon="swap_horiz" onClick={() => handleOpenReplaceModal(item)} btnColor="#344767" tooltip="Replace Item" />
          </MDBox>
        );
      case 'In Repair':
        return (
          <MDBox sx={containerSx}>
            {renderRetireButton(item, isListView)}
            {renderHistoryButton(item, isListView)}
            <ActionIcon icon="edit" onClick={() => handleOpenItemEditModal(item)} btnColor="#7b809a" tooltip="Edit Item" />
            <ActionIcon icon="check_circle" onClick={() => handleOpenFoundModal(item)} btnColor="#4CAF50" tooltip="Return to Good" />
            <ActionIcon icon="swap_horiz" onClick={() => handleOpenReplaceModal(item)} btnColor="#344767" tooltip="Replace Item" />
          </MDBox>
        );
      default:
        return (
          <MDBox sx={containerSx}>
            {renderRetireButton(item, isListView)}
            {renderHistoryButton(item, isListView)}
            <ActionIcon icon="edit" onClick={() => handleOpenItemEditModal(item)} btnColor="#7b809a" tooltip="Edit Item" />
            <ActionIcon icon="settings" onClick={() => { }} btnColor={color} tooltip="Manage Settings" />
          </MDBox>
        );
    }
  };

  const Container = standalone ? DashboardLayout : MDBox;

  return (
    <Container {...(!standalone && { sx: { p: 3 } })}>
      {standalone && <DashboardNavbar />}
      {renderPreviewModal()}

      {/* Top Action Buttons Section & Stats */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "center", md: "flex-end" }}
        flexDirection={{ xs: "column", md: "row" }}
        width="100%"
        px={3}
        pt={4}
        pb={2}
        gap={{ xs: 2, md: 3 }}
      >
        {/* Inventory Stats */}
        <MDBox display="flex" alignItems="center" justifyContent="center" gap={{ xs: 3, md: 4 }}>
          <MDBox display="flex" flexDirection="column" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1A73E8", lineHeight: 1 }}>
              {items.filter(item => {
                const st = (item.fields["Item Status"] || "Good").toString().trim().toLowerCase();
                return st !== "retired";
              }).length}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#7b809a", fontWeight: "500", mt: 0.5 }}>
              Total
            </MDTypography>
          </MDBox>
          <MDBox display="flex" flexDirection="column" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#4CAF50", lineHeight: 1 }}>
              {items.filter(item => (item.fields["Item Status"] || "Good").toString().toLowerCase().trim() === "good").length}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#7b809a", fontWeight: "500", mt: 0.5 }}>
              Good
            </MDTypography>
          </MDBox>
          <MDBox display="flex" flexDirection="column" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#FF9800", lineHeight: 1 }}>
              {items.filter(item => {
                const st = (item.fields["Item Status"] || "").toString().toLowerCase().trim();
                return ["damaged", "in repair", "misplaced"].includes(st);
              }).length}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#7b809a", fontWeight: "500", mt: 0.5 }}>
              Attention
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Buttons — visible but disabled for view-only users */}
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems="center"
          gap={1.5}
          width={{ xs: "100%", sm: "auto" }}
        >
          <MDButton
            disabled={isInventoryViewOnly}
            variant="outlined"
            color="secondary"
            onClick={handleOpenManageCategoriesModal}
            sx={{
              textTransform: "none",
              fontWeight: "500",
              padding: { xs: "8px 14px", md: "8px 24px" },
              fontSize: { xs: "12px", md: "14px" },
              backgroundColor: "#ffffff",
              whiteSpace: "nowrap",
              width: { xs: "100%", sm: "auto" },
              color: isInventoryViewOnly ? "#adb5bd" : "inherit",
              borderColor: isInventoryViewOnly ? "#d2d6da" : "inherit",
              "&:hover": {
                backgroundColor: isInventoryViewOnly ? "transparent" : "rgba(123, 128, 154, 0.05)",
                boxShadow: isInventoryViewOnly ? "none" : "0 4px 6px -1px rgba(0,0,0,0.05)",
              },
              "&.Mui-disabled": {
                color: "#adb5bd",
                borderColor: "#d2d6da",
                opacity: 0.7
              }
            }}
          >
            <span className="material-icons" style={{ fontSize: "16px", marginRight: "4px" }}>settings</span>
            Manage Categories
          </MDButton>

          <MDButton
            disabled={isInventoryViewOnly}
            variant="outlined"
            color="dark"
            onClick={handleOpenCategoryModal}
            sx={{
              textTransform: "none",
              fontWeight: "500",
              padding: { xs: "8px 14px", md: "8px 24px" },
              fontSize: { xs: "12px", md: "14px" },
              backgroundColor: "#ffffff",
              whiteSpace: "nowrap",
              width: { xs: "100%", sm: "auto" },
              color: isInventoryViewOnly ? "#adb5bd" : "inherit",
              borderColor: isInventoryViewOnly ? "#d2d6da" : "inherit",
              "&:hover": {
                backgroundColor: isInventoryViewOnly ? "transparent" : "rgba(52, 71, 103, 0.05)",
                boxShadow: isInventoryViewOnly ? "none" : "0 4px 6px -1px rgba(0,0,0,0.05)",
              },
              "&.Mui-disabled": {
                color: "#adb5bd",
                borderColor: "#d2d6da",
                opacity: 0.7
              }
            }}
          >
            <span className="material-icons" style={{ fontSize: "16px", marginRight: "4px" }}>add</span>
            Add Category
          </MDButton>

          <MDButton
            disabled={isInventoryViewOnly}
            variant="outlined"
            color="info"
            onClick={handleOpenItemModal}
            sx={{
              textTransform: "none",
              fontWeight: "500",
              padding: { xs: "8px 14px", md: "8px 24px" },
              fontSize: { xs: "12px", md: "14px" },
              backgroundColor: "#ffffff",
              whiteSpace: "nowrap",
              width: { xs: "100%", sm: "auto" },
              color: isInventoryViewOnly ? "#adb5bd" : "inherit",
              borderColor: isInventoryViewOnly ? "#d2d6da" : "inherit",
              "&:hover": {
                backgroundColor: isInventoryViewOnly ? "transparent" : "rgba(26, 115, 232, 0.05)",
                boxShadow: isInventoryViewOnly ? "none" : "0 4px 6px -1px rgba(26, 115, 232, 0.1)",
              },
              "&.Mui-disabled": {
                color: "#adb5bd",
                borderColor: "#d2d6da",
                opacity: 0.7
              }
            }}
          >
            <span className="material-icons" style={{ fontSize: "16px", marginRight: "4px" }}>add</span>
            Add Item
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Premium Filter & Stats Header */}
      <MDBox pt={3} pb={3}>
        <Card sx={{
          borderRadius: { xs: "20px", md: "100px" },
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(234, 238, 243, 0.5)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
          overflow: "visible",
          p: { xs: 2, md: 0.75 },
          mx: 3,
          mt: -2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 2, md: 0 }
        }}>
          <MDBox display="flex" width="100%" alignItems="center" px={1} gap={1} flexDirection={{ xs: "column", md: "row" }}>
            {/* Property Filter */}
            <MDBox sx={{ flex: 1, minWidth: { xs: "100%", md: "140px" }, position: "relative", display: "flex", alignItems: "center", "&:hover": { backgroundColor: "rgba(52, 71, 103, 0.03)", borderRadius: "10px" }, transition: "all 0.2s" }}>
              <Select
                fullWidth
                value={selectedApartment}
                onChange={handleApartmentChange}
                displayEmpty
                variant="outlined"
                IconComponent={() => null}
                renderValue={(selected) => (
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, color: "#344767" }}>
                    <span className="material-icons" style={{ fontSize: "16px", color: "#7b809a" }}>apartment</span>
                    <span>{selected || "All Properties"}</span>
                  </MDBox>
                )}
                sx={{
                  ...dropdownSelectSx,
                  border: { xs: "1px solid #e0e0e0", md: "none" },
                  boxShadow: "none",
                  "& fieldset": { border: "none" },
                  backgroundColor: "transparent",
                  height: "36px",
                  fontSize: "0.75rem",
                  "& .MuiSelect-select": { padding: "0 24px 0 0 !important", display: 'flex', alignItems: 'center' }
                }}
              >
                <MenuItem value="All Properties">All Properties</MenuItem>
                {apartments.map((name, index) => (
                  <MenuItem key={index} value={name}>{name}</MenuItem>
                ))}
              </Select>
              <MDBox sx={{ pr: 1.5, display: "flex", alignItems: "center", color: "#344767", position: "absolute", right: 0, pointerEvents: "none", zIndex: 1 }}>
                <span className="material-icons" style={{ fontSize: "20px" }}>expand_more</span>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "20px", width: "1px", backgroundColor: "#e0e0e0", display: { xs: "none", md: "block" } }} />

            {/* Category Filter */}
            <MDBox sx={{ flex: 1, minWidth: { xs: "100%", md: "140px" }, position: "relative", display: "flex", alignItems: "center", "&:hover": { backgroundColor: "rgba(52, 71, 103, 0.03)", borderRadius: "10px" }, transition: "all 0.2s" }}>
              <Select
                fullWidth
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty
                IconComponent={() => null}
                renderValue={(selected) => (
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, color: "#344767" }}>
                    <span className="material-icons" style={{ fontSize: "16px", color: "#7b809a" }}>category</span>
                    <span>{selected || "All Categories"}</span>
                  </MDBox>
                )}
                sx={{
                  ...dropdownSelectSx,
                  border: { xs: "1px solid #e0e0e0", md: "none" },
                  "& fieldset": { border: "none" },
                  backgroundColor: "transparent",
                  height: "36px",
                  fontSize: "0.75rem",
                  "& .MuiSelect-select": { padding: "0 24px 0 0 !important", display: 'flex', alignItems: 'center' }
                }}
              >
                <MenuItem value="All Categories">All Categories</MenuItem>
                {categories.map((name, index) => (
                  <MenuItem key={index} value={name}>{name}</MenuItem>
                ))}
              </Select>
              <MDBox sx={{ pr: 1.5, display: "flex", alignItems: "center", color: "#344767", position: "absolute", right: 0, pointerEvents: "none", zIndex: 1 }}>
                <span className="material-icons" style={{ fontSize: "20px" }}>expand_more</span>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "20px", width: "1px", backgroundColor: "#e0e0e0", display: { xs: "none", md: "block" } }} />

            {/* Status Filter */}
            <MDBox sx={{ flex: 1, minWidth: { xs: "100%", md: "140px" }, position: "relative", display: "flex", alignItems: "center", "&:hover": { backgroundColor: "rgba(52, 71, 103, 0.03)", borderRadius: "10px" }, transition: "all 0.2s" }}>
              <Select
                fullWidth
                value={selectedStatus}
                onChange={handleStatusChange}
                displayEmpty
                IconComponent={() => null}
                renderValue={(selected) => (
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, color: "#344767" }}>
                    <span className="material-icons" style={{ fontSize: "16px", color: "#7b809a" }}>checklist</span>
                    <span>{selected || "All Status"}</span>
                  </MDBox>
                )}
                sx={{
                  ...dropdownSelectSx,
                  border: { xs: "1px solid #e0e0e0", md: "none" },
                  "& fieldset": { border: "none" },
                  backgroundColor: "transparent",
                  height: "36px",
                  fontSize: "0.75rem",
                  "& .MuiSelect-select": { padding: "0 24px 0 0 !important", display: 'flex', alignItems: 'center' }
                }}
              >
                <MenuItem value="All Status">All Status</MenuItem>
                <MenuItem value="Good">Good</MenuItem>
                <MenuItem value="Damaged">Damaged</MenuItem>
                <MenuItem value="Misplaced">Misplaced</MenuItem>
                <MenuItem value="In Repair">In Repair</MenuItem>
                <MenuItem value="Retired">Retired</MenuItem>
              </Select>
              <MDBox sx={{ pr: 1.5, display: "flex", alignItems: "center", color: "#344767", position: "absolute", right: 0, pointerEvents: "none", zIndex: 1 }}>
                <span className="material-icons" style={{ fontSize: "20px" }}>expand_more</span>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "20px", width: "1px", backgroundColor: "#e0e0e0", display: { xs: "none", md: "block" } }} />

            {/* Search Input */}
            <MDBox sx={{ flex: 2, display: "flex", alignItems: "center", px: { xs: 0, md: 1 }, width: "100%" }}>
              <span className="material-icons" style={{ color: '#7b809a', fontSize: '18px', marginRight: '8px' }}>search</span>
              <TextField
                fullWidth
                placeholder="Search items..."
                value={searchTerm}
                onChange={handleSearchChange}
                variant="standard"
                InputProps={{ disableUnderline: true, sx: { fontSize: "0.8rem" } }}
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Page Content area */}
      <MDBox pt={2} pb={3}>
        {loading ? (
          <Card sx={{ minHeight: "50vh", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent", boxShadow: "none" }}>
            <MDBox textAlign="center">
              <CircularProgress color="info" size={40} />
              <MDTypography variant="h6" color="text" sx={{ mt: 2 }}>
                Loading Inventory...
              </MDTypography>
            </MDBox>
          </Card>
        ) : Object.keys(displayGroupedItems).length === 0 ? (
          <Card sx={{ minHeight: "50vh", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent", boxShadow: "none", border: "1px dashed #d2d6da" }}>
            <MDBox textAlign="center">
              <span className="material-icons" style={{ fontSize: '48px', color: '#d2d6da', marginBottom: '16px' }}>inventory_2</span>
              <MDTypography variant="h5" color="text" fontWeight="medium">
                No items found
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                Try adjusting your filters or add a new item.
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <MDBox mb={5} sx={{ maxWidth: "1100px", mx: "auto" }}>
            {Object.entries(displayGroupedItems).map(([propertyName, propertyItems]) => {
              const counts = propertyItems.reduce((acc, item) => {
                // Reuse robust status extraction logic
                const getStatusVal = (v) => {
                  if (v === null || v === undefined) return "Good";
                  if (Array.isArray(v)) {
                    const first = v[0];
                    if (typeof first === 'object' && first !== null) return first.name || first.value || first.id || "Good";
                    return first || "Good";
                  }
                  if (typeof v === 'object') return v.name || v.value || v.id || "Good";
                  return v.toString();
                };
                const status = getStatusVal(item.fields["Item Status"]).trim().toLowerCase();
                acc[status] = (acc[status] || 0) + 1;
                return acc;
              }, {});

              return (
                <MDBox key={propertyName} mb={4}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    mb={3} px={1} gap={1}
                  >
                    <MDTypography variant="h4" fontWeight="bold" color="dark">
                      {propertyName}
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {['Good', 'Damaged', 'Misplaced', 'In Repair', 'Retired'].map((label) => {
                        const count = counts[label.toLowerCase()] || 0;
                        if (count === 0) return null;

                        const status = label; // Display label
                        const color = getStatusColor(status);

                        return (
                          <MDBox
                            key={status}
                            sx={{
                              backgroundColor: `${getStatusColor(status)}15`,
                              border: `1px solid ${getStatusColor(status)}`,
                              borderRadius: "6px",
                              px: 1.5,
                              py: 0.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1
                            }}
                          >
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: getStatusColor(status) }}>
                              {count}
                            </MDTypography>
                            <MDTypography variant="caption" fontWeight="medium" sx={{ color: getStatusColor(status), textTransform: "capitalize" }}>
                              {status}
                            </MDTypography>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {/* List Header Row - Desktop Only */}
                    <MDBox
                      display={{ xs: "none", md: "flex" }}
                      alignItems="center"
                      justifyContent="space-between"
                      px={2}
                      py={0.75}
                      sx={{
                        backgroundColor: "#fcfdfe",
                        borderRadius: "10px 10px 0 0",
                        borderBottom: "1px solid #f0f2f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                      }}
                    >
                      <MDBox display="flex" alignItems="center" width="100%">
                        <MDTypography variant="overline" color="text" sx={{ flex: 1.5, minWidth: "180px", fontSize: "0.6rem", fontWeight: "bold", letterSpacing: "0.5px", pl: 1 }}>ITEM NAME / CATEGORY</MDTypography>
                        <MDTypography variant="overline" color="text" sx={{ width: "110px", textAlign: "center", fontSize: "0.6rem", fontWeight: "bold", letterSpacing: "0.5px" }}>STATUS</MDTypography>
                        <MDTypography variant="overline" color="text" sx={{ width: "80px", textAlign: "center", fontSize: "0.6rem", fontWeight: "bold", letterSpacing: "0.5px" }}>QTY</MDTypography>
                        <MDTypography variant="overline" color="text" sx={{ width: "100px", textAlign: "center", fontSize: "0.6rem", fontWeight: "bold", letterSpacing: "0.5px" }}>PRICE (PKR)</MDTypography>
                        <MDTypography variant="overline" color="text" sx={{ width: "110px", textAlign: "center", fontSize: "0.6rem", fontWeight: "bold", letterSpacing: "0.5px" }}>ADDED DATE</MDTypography>
                        <MDTypography variant="overline" color="text" sx={{ flex: 1.2, textAlign: "right", fontSize: "0.6rem", fontWeight: "bold", pr: 2, letterSpacing: "0.5px" }}>ACTIONS</MDTypography>
                      </MDBox>
                    </MDBox>

                    {propertyItems.map(item => {
                      const fields = item.fields;
                      const status = fields["Item Status"] || "Good";
                      const color = getStatusColor(status);
                      const icon = getStatusIcon(status);

                      return (
                        <Card
                          key={item.id}
                          sx={{
                            borderLeft: `4px solid ${color}`, // Thicker indicator
                            borderRadius: "6px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                            backgroundColor: "#ffffff",
                            overflow: "hidden",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            mb: 1, // Increased spacing between items
                            "&:hover": {
                              backgroundColor: "#fcfdff",
                              transform: "translateX(2px)",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.05)"
                            }
                          }}
                        >
                          <MDBox
                            p={{ xs: 1.5, md: 1.25 }} // Increased vertical padding for more height
                            px={{ md: 2 }}
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", md: "row" },
                              alignItems: { xs: "flex-start", md: "center" },
                              gap: { xs: 1.5, md: 2 }
                            }}
                          >
                            {/* Item Identity Row */}
                            <MDBox sx={{ flex: 1.5, minWidth: { md: "180px" }, width: "100%" }}>
                              <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <MDBox>
                                  <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.85rem", lineHeight: 1.2, display: "block" }}>
                                    {fields["Item Name"]}
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" mt={0.2} gap={0.5}>
                                    <span className="material-icons" style={{ fontSize: "13px", color: "#7b809a" }}>label</span>
                                    <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: "0.7rem" }}>
                                      {fields["Category"]}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>

                                {/* Mobile Status Indicator */}
                                <MDBox sx={{ display: { md: "none" } }}>
                                  <MDBox display="flex" alignItems="center" px={1} py={0.25} sx={{ backgroundColor: `${color}15`, borderRadius: "4px", border: `1px solid ${color}50` }}>
                                    <span className="material-icons" style={{ fontSize: "12px", color: color, marginRight: "4px" }}>{icon}</span>
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: color, fontSize: "0.65rem" }}>
                                      {status}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                            </MDBox>

                            {/* Desktop Status Pill */}
                            <MDBox sx={{ width: "110px", display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
                              <MDBox display="flex" alignItems="center" px={1.25} py={0.25} sx={{ backgroundColor: `${color}08`, borderRadius: "100px", border: `1px solid ${color}20` }}>
                                <span className="material-icons" style={{ fontSize: "11px", color: color, marginRight: "4px" }}>{icon}</span>
                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: color, fontSize: "0.62rem" }}>
                                  {status}
                                </MDTypography>
                              </MDBox>
                            </MDBox>

                            {/* Metrics Container */}
                            <MDBox
                              sx={{
                                display: "flex",
                                width: { xs: "100%", md: "310px" },
                                justifyContent: { xs: "space-between", md: "space-around" },
                                alignItems: "center",
                                backgroundColor: { xs: "#f8f9fa", md: "transparent" },
                                p: { xs: 1, md: 0 },
                                borderRadius: { xs: "6px", md: 0 }
                              }}
                            >
                              <MDBox sx={{ width: { md: "80px" }, textAlign: { md: "center" } }}>
                                <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ display: { md: "none" }, mr: 1, fontSize: "0.6rem" }}>QTY:</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.72rem" }}>
                                  {fields["Quantity"]}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" sx={{ ml: 0.2, fontSize: "0.55rem" }}>{fields["Unit"] || "pcs"}</MDTypography>
                              </MDBox>

                              <MDBox sx={{ width: { md: "100px" }, textAlign: { md: "center" } }}>
                                <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ display: { md: "none" }, mr: 1, fontSize: "0.6rem" }}>PRICE:</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.72rem" }}>
                                  {fields["Cost"] || fields["Cost "] || "0"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" sx={{ ml: 0.2, fontSize: "0.55rem" }}>PKR</MDTypography>
                              </MDBox>

                              <MDBox sx={{ width: { md: "110px" }, textAlign: { md: "center" } }}>
                                <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ display: { md: "none" }, mr: 1, fontSize: "0.6rem" }}>DATE:</MDTypography>
                                <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ fontSize: "0.65rem" }}>
                                  {fields["Created at"] ? new Date(fields["Created at"]).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                                </MDTypography>
                              </MDBox>
                            </MDBox>

                            {/* Action Buttons */}
                            <MDBox sx={{ flex: 1.2, width: "100%" }}>
                              {renderActionButtons(status, color, item, true)}
                            </MDBox>
                          </MDBox>
                        </Card>
                      );
                    })}
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </MDBox>

      {/* ====== ADD CATEGORY MODAL ====== */}
      <Dialog
        open={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "24px",
            padding: { xs: "16px", sm: "24px" },
            width: { xs: "95%", sm: "400px" },
            margin: { xs: "16px", sm: "auto" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "24px", paddingTop: "8px", fontWeight: "bold", color: "#2c3e50", borderBottom: "none", fontSize: "1.5rem" }}>
          Add New Category
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "0 !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* ID Field - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID *
            </MDTypography>
            <TextField
              value={nextCategoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ color: "#7b809a", fontWeight: "bold", marginBottom: "8px", fontSize: "0.75rem", letterSpacing: "1px" }}>
              NAME *
            </MDTypography>
            <TextField
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              disabled={isInventoryViewOnly}
              variant="outlined"
              autoFocus
              fullWidth
              placeholder="Enter category name"
              sx={{
                "& .MuiInputBase-input": { color: "#495057", fontSize: "0.875rem", padding: "12px" },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  "& fieldset": { borderColor: "#d2d6da" },
                  "&:hover fieldset": { borderColor: "#7b809a" },
                  "&.Mui-focused fieldset": { borderColor: "#1A73E8" }
                }
              }}
            />
          </MDBox>

          {/* Created At Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ color: "#7b809a", fontWeight: "bold", marginBottom: "8px", fontSize: "0.75rem", letterSpacing: "1px" }}>
              CREATED AT *
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a", fontSize: "0.875rem", padding: "12px" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "12px" }
              }}
            />
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "24px", paddingTop: "8px", borderTop: "none", gap: 2, justifyContent: "flex-end" }}>
          <MDButton
            onClick={handleCloseCategoryModal}
            sx={{
              color: "#7b809a",
              border: "1.5px solid #d2d6da",
              textTransform: "uppercase",
              fontWeight: "bold",
              fontSize: "0.75rem",
              px: 4,
              py: 1,
              borderRadius: "10px",
              backgroundColor: "transparent",
              '&:hover': { backgroundColor: "#f8f9fa", borderColor: "#7b809a" }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveCategory}
            disabled={isInventoryViewOnly}
            sx={{
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              fontWeight: "bold",
              px: 4,
              py: 1,
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
              "&.Mui-disabled": {
                backgroundColor: "#f8f9fa",
                color: "#adb5bd !important",
                boxShadow: "none",
              }
            }}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isItemModalOpen}
        onClose={handleCloseItemModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: { xs: "8px", sm: "16px" },
            width: { xs: "95%", sm: "auto" },
            margin: { xs: "16px", sm: "32px" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Add New Item
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Property / Listing Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              PROPERTY *
            </MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={newItemListingName}
                onChange={(e) => setNewItemListingName(e.target.value)}
                disabled={isInventoryViewOnly}
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d2d6da",
                  "& .MuiSelect-select": { padding: "8.5px 14px" },
                  "& fieldset": { border: "none" },
                }}
              >
                <MenuItem disabled value="">
                  Select a property
                </MenuItem>
                {apartments.map((name, index) => (
                  <MenuItem key={index} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>

          {/* Item Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ITEM NAME *
            </MDTypography>
            <TextField
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              disabled={isInventoryViewOnly}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input": { color: "#495057", fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "#d2d6da" },
                  "&:hover fieldset": { borderColor: "#7b809a" },
                  "&.Mui-focused fieldset": { borderColor: "#1A73E8" }
                }
              }}
            />
          </MDBox>

          {/* Item Status Field - Always Good for New Items */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ITEM STATUS
            </MDTypography>
            <TextField
              value="Good"
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a", fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Category Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              CATEGORY *
            </MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                disabled={isInventoryViewOnly}
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d2d6da",
                  "& .MuiSelect-select": { padding: "8.5px 14px" },
                  "& fieldset": { border: "none" },
                }}
              >
                <MenuItem disabled value="">
                  Select a category
                </MenuItem>
                {categories.map((cat, index) => (
                  <MenuItem key={index} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>

          {/* Quantity & Unit Row */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
                  QUANTITY *
                </MDTypography>
                <TextField
                  type="number"
                  value={newItemQuantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || Number(val) >= 0) {
                      setNewItemQuantity(val);
                    }
                  }}
                  disabled={isInventoryViewOnly}
                  inputProps={{ min: 0 }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputBase-input": { color: "#495057", fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#d2d6da" } }
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
                  UNIT *
                </MDTypography>
                <TextField
                  value="pcs"
                  disabled
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a", fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                    "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>

          {/* Cost Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              COST
            </MDTypography>
            <TextField
              type="number"
              value={newItemCost}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || Number(val) >= 0) {
                  setNewItemCost(val);
                }
              }}
              disabled={isInventoryViewOnly}
              inputProps={{ min: 0 }}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input": { color: "#495057", fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#d2d6da" } }
              }}
            />
          </MDBox>

          {/* Created At Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              CREATED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a", fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachment Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "2px dashed #d2d6da",
                borderRadius: "12px",
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": { borderColor: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.02)" }
              }}
              onClick={() => document.getElementById("add-item-files").click()}
            >
              <input
                id="add-item-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setNewItemAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "32px", color: "#1A73E8", marginBottom: "8px" }}>cloud_upload</span>
              <MDTypography variant="button" fontWeight="medium" display="block">
                Click to Upload Images
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                Select multiple images (No limit)
              </MDTypography>
            </MDBox>

            {newItemAttachments.length > 0 && (
              <MDBox mt={2} sx={{ maxHeight: "150px", overflowY: "auto", pr: 1 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="caption" fontWeight="bold" color="info">
                    {newItemAttachments.length} Files Ready
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="error"
                    sx={{ cursor: "pointer", fontWeight: "bold" }}
                    onClick={() => setNewItemAttachments([])}
                  >
                    Clear All
                  </MDTypography>
                </MDBox>
                <Grid container spacing={1}>
                  {newItemAttachments.map((file, idx) => (
                    <Grid item xs={12} key={idx}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={1.5} py={0.5}
                        sx={{ backgroundColor: "#f8f9fa", borderRadius: "6px" }}
                      >
                        <MDTypography variant="caption" color="dark" noWrap sx={{ maxWidth: "80%" }}>
                          {file.name}
                        </MDTypography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewItemAttachments(newItemAttachments.filter((_, i) => i !== idx));
                          }}
                        >
                          <span className="material-icons" style={{ fontSize: "16px", color: "#F44336" }}>cancel</span>
                        </IconButton>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "24px 32px", borderTop: "none", gap: 2, justifyContent: "center", display: "flex" }}>
          <MDButton
            onClick={handleCloseItemModal}
            variant="outlined"
            color="secondary"
            sx={{
              flex: 1,
              height: "44px",
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: "500",
              fontSize: "0.9rem"
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveItem}
            disabled={isInventoryViewOnly || isSubmittingItem}
            variant="gradient"
            color="info"
            sx={{
              flex: 2,
              height: "44px",
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: "bold",
              fontSize: "0.9rem",
              boxShadow: "0 4px 12px rgba(26, 115, 232, 0.2)"
            }}
          >
            {isSubmittingItem ? (
              <MDBox display="flex" alignItems="center">
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                Adding...
              </MDBox>
            ) : (
              "Add to Inventory"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ======== Damage Report Modal ======== */}
      <Dialog
        open={isDamageModalOpen}
        onClose={handleCloseDamageModal}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "400px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Report Damage: {selectedItemForDamage?.fields["Item Name"]}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Username Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              REPORTED BY
            </MDTypography>
            <TextField
              value={user?.name || user?.username || "System"}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* ID Field - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID
            </MDTypography>
            <TextField
              value={nextHistoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Recorded At Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RECORDED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>


          {/* Reservation ID Dropdown - Dynamic */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RESERVATION ID
            </MDTypography>
            <Select
              value={damageReservationId}
              onChange={(e) => setDamageReservationId(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">
                {isFetchingReservations ? "Loading reservations..." : recentReservations.length === 0 ? "No active reservations found" : "Select Reservation"}
              </MenuItem>
              {recentReservations.map((res) => (
                <MenuItem key={res.id} value={String(res.fields["Reservation ID"])}>
                  {res.fields["Guest Name"] ? `${res.fields["Guest Name"]} (${res.fields["Reservation ID"]})` : res.fields["Reservation ID"]}
                </MenuItem>
              ))}
            </Select>
          </MDBox>

          {/* Description Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              DESCRIPTION
            </MDTypography>
            <TextField
              value={damageDescription}
              onChange={(e) => setDamageDescription(e.target.value)}
              placeholder="Enter damage description"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachments Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#F44336", backgroundColor: "rgba(244, 67, 54, 0.02)" }
              }}
              onClick={() => document.getElementById("damage-files").click()}
            >
              <input
                id="damage-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setDamageAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#F44336" }}>photo_library</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Add Damage Images
              </MDTypography>
            </MDBox>

            {damageAttachments.length > 0 && (
              <MDBox mt={1.5} sx={{ maxHeight: "120px", overflowY: "auto" }}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="caption" fontWeight="bold">{damageAttachments.length} files</MDTypography>
                  <MDTypography variant="caption" color="error" sx={{ cursor: "pointer" }} onClick={() => setDamageAttachments([])}>Clear</MDTypography>
                </MDBox>
                {damageAttachments.map((file, idx) => (
                  <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa" }}>
                    <MDTypography variant="caption" noWrap sx={{ maxWidth: "80%" }}>{file.name}</MDTypography>
                    <span className="material-icons" style={{ fontSize: "14px", color: "#F44336", cursor: "pointer" }} onClick={() => setDamageAttachments(damageAttachments.filter((_, i) => i !== idx))}>close</span>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ paddingTop: "12px", paddingBottom: "12px", gap: 1 }}>
          <MDButton onClick={handleCloseDamageModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveDamageReport}
            color="error"
            variant="gradient"
            disabled={isSubmittingDamage}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingDamage ? "Confirming..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ======== Retire Item Modal ======== */}
      <Dialog
        open={isRetireModalOpen}
        onClose={handleCloseRetireModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "24px",
            padding: "16px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1, fontWeight: "bold", color: "#344767" }}>
          Retire Item
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1 }}>
          <MDTypography variant="button" fontWeight="medium" display="block" textAlign="center" color="secondary" sx={{ mb: 3 }}>
            {selectedItemForRetire?.fields["Item Name"]}
          </MDTypography>

          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDBox>
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.7, mb: 0.5, display: "block", fontSize: "0.65rem" }}>
                REPORTED BY
              </MDTypography>
              <TextField
                value={user?.name || user?.username || "System"}
                disabled
                fullWidth
                size="small"
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" } }}
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.7, mb: 0.5, display: "block", fontSize: "0.65rem" }}>
                RECORDED AT
              </MDTypography>
              <TextField
                value={getPakistanTime()}
                disabled
                fullWidth
                size="small"
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" } }}
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.7, mb: 0.5, display: "block", fontSize: "0.65rem" }}>
                DESCRIPTION
              </MDTypography>
              <TextField
                value={retireDescription}
                onChange={(e) => setRetireDescription(e.target.value)}
                placeholder="Reason for retirement..."
                fullWidth
                multiline
                rows={3}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, px: 2, pb: 2, pt: 1, borderTop: "none" }}>
          <MDButton
            onClick={handleCloseRetireModal}
            sx={{
              width: "140px",
              height: "44px",
              borderRadius: "10px",
              border: "1.5px solid #d2d6da",
              color: "#7b809a",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              backgroundColor: "transparent",
              boxShadow: "none",
              '&:hover': { backgroundColor: "#f8f9fa", borderColor: "#7b809a" }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveRetireReport}
            disabled={isSubmittingRetire}
            sx={{
              width: "140px",
              height: "44px",
              borderRadius: "10px",
              border: "1.5px solid #F44336",
              backgroundColor: "#feebee !important",
              color: "#F44336 !important",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              boxShadow: "none",
              '&:hover': { backgroundColor: "#fde0e0 !important" }
            }}
          >
            {isSubmittingRetire ? "Retiring..." : "Retire Item"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "12px", md: "16px" },
            m: { xs: 2, md: 4 },
            width: { xs: "calc(100% - 32px)", md: "auto" },
            overflow: "hidden",
            backgroundColor: "#ffffff",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <MDBox sx={{ p: { xs: 2, md: 3 }, borderBottom: "1px solid #f0f2f5" }}>
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767", mb: 1 }}>
            {selectedItemForHistory?.fields["Item Name"]}
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDBox
              sx={{
                backgroundColor: getStatusColor(selectedItemForHistory?.fields["Item Status"]) + "1A",
                color: getStatusColor(selectedItemForHistory?.fields["Item Status"]),
                px: 1.5, py: 0.5,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: `1px solid ${getStatusColor(selectedItemForHistory?.fields["Item Status"])}4D`
              }}
            >
              <span className="material-icons" style={{ fontSize: "14px" }}>
                {getStatusIcon(selectedItemForHistory?.fields["Item Status"])}
              </span>
              <MDTypography variant="caption" fontWeight="bold" color="inherit" sx={{ textTransform: "capitalize" }}>
                {selectedItemForHistory?.fields["Item Status"]}
              </MDTypography>
            </MDBox>
            <MDBox
              sx={{
                backgroundColor: "#f0f2f5",
                color: "#344767",
                px: 1.5, py: 0.5,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: "1px solid #d2d6da"
              }}
            >
              <span className="material-icons" style={{ fontSize: "14px" }}>category</span>
              <MDTypography variant="caption" fontWeight="bold">
                {selectedItemForHistory?.fields["Category"]}
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ ml: { xs: 0, sm: "auto" }, fontWeight: "medium", mt: { xs: 1, sm: 0 }, width: { xs: "100%", sm: "auto" } }}>
              Added {selectedItemForHistory?.fields["Created at"] ? new Date(selectedItemForHistory?.fields["Created at"]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
            </MDTypography>
          </MDBox>
        </MDBox>

        <DialogContent sx={{ p: 0, maxHeight: "60vh" }}>
          <MDBox sx={{ p: { xs: 2, md: 3 } }}>
            {itemHistoryRecords.length === 0 ? (
              <MDBox textAlign="center" py={4}>
                <MDTypography variant="button" color="text">No history records found.</MDTypography>
              </MDBox>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={3}>
                {itemHistoryRecords.map((record, index) => {
                  const status = record.fields["Item Status"];
                  const statusColor = getStatusColor(status);
                  return (
                    <MDBox key={index} display="flex" gap={2}>
                      <MDBox sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <MDBox
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#ffffff",
                            border: `2px solid ${statusColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2
                          }}
                        >
                          <span className="material-icons" style={{ fontSize: "16px", color: statusColor }}>
                            {getStatusIcon(status)}
                          </span>
                        </MDBox>
                        {index < itemHistoryRecords.length - 1 && (
                          <MDBox sx={{ width: "2px", flex: 1, backgroundColor: "#f0f2f5", my: 0.5 }} />
                        )}
                      </MDBox>
                      <MDBox sx={{ flex: 1, pb: index < itemHistoryRecords.length - 1 ? 2 : 0 }}>
                        <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                          <span className="material-icons" style={{ fontSize: "16px", color: statusColor }}>
                            {getStatusIcon(status)}
                          </span>
                          <MDTypography variant="button" fontWeight="bold" sx={{ color: statusColor, letterSpacing: "0.5px" }}>
                            {status}
                          </MDTypography>
                        </MDBox>

                        <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                          <MDTypography variant="caption" fontWeight="medium" color="dark">
                            {record.fields["Name"] || "System"}
                          </MDTypography>
                          <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                            • {record.fields["Listing Name"] || "Staff"}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" sx={{ color: "#344767", display: "block", mb: 0.5, fontWeight: "medium" }}>
                          {record.fields["Description"] || ""}
                        </MDTypography>
                        {(record.fields["Price"] || record.fields["Send to"] || record.fields["Reservation ID"] || record.fields["Quantity"] || record.fields["Guest Name"]) && (
                          <MDBox display="flex" flexWrap="wrap" gap={1.5} mb={0.5} mt={0.5}>
                            {record.fields["Quantity"] && (
                              <MDBox display="flex" alignItems="center" gap={0.5} sx={{ backgroundColor: "rgba(156, 39, 176, 0.05)", px: 1, py: 0.25, borderRadius: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "14px", color: "#9C27B0" }}>shopping_cart</span>
                                <MDTypography variant="caption" sx={{ color: "#9C27B0", fontWeight: "medium", letterSpacing: "0.5px" }}>
                                  Qty: {record.fields["Quantity"]}
                                </MDTypography>
                              </MDBox>
                            )}
                            {record.fields["Guest Name"] && (
                              <MDBox display="flex" alignItems="center" gap={0.5} sx={{ backgroundColor: "rgba(76, 175, 80, 0.05)", px: 1, py: 0.25, borderRadius: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "14px", color: "#4CAF50" }}>person</span>
                                <MDTypography variant="caption" sx={{ color: "#4CAF50", fontWeight: "medium", letterSpacing: "0.5px" }}>
                                  Guest Name: {record.fields["Guest Name"]}
                                </MDTypography>
                              </MDBox>
                            )}
                            {record.fields["Reservation ID"] && (
                              <MDBox display="flex" alignItems="center" gap={0.5} sx={{ backgroundColor: "rgba(26, 115, 232, 0.05)", px: 1, py: 0.25, borderRadius: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "14px", color: "#1A73E8" }}>receipt_long</span>
                                <MDTypography variant="caption" sx={{ color: "#1A73E8", fontWeight: "medium", letterSpacing: "0.5px" }}>
                                  Reservation ID: {record.fields["Reservation ID"]}
                                </MDTypography>
                              </MDBox>
                            )}
                            {record.fields["Send to"] && (
                              <MDBox display="flex" alignItems="center" gap={0.5} sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)", px: 1, py: 0.25, borderRadius: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "14px", color: "#344767" }}>engineering</span>
                                <MDTypography variant="caption" sx={{ color: "#344767", fontWeight: "medium" }}>
                                  Send to: {record.fields["Send to"]}
                                </MDTypography>
                              </MDBox>
                            )}
                            {record.fields["Price"] && (
                              <MDBox display="flex" alignItems="center" gap={0.5} sx={{ backgroundColor: "rgba(76, 175, 80, 0.05)", px: 1, py: 0.25, borderRadius: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "14px", color: "#4CAF50" }}>payments</span>
                                <MDTypography variant="caption" sx={{ color: "#4CAF50", fontWeight: "medium", letterSpacing: "0.5px" }}>
                                  PKR {record.fields["Price"]}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        )}
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                          <MDTypography variant="caption" sx={{ color: "#7b809a", fontSize: "0.7rem" }}>
                            {record.fields["Recorded at"] ? new Date(record.fields["Recorded at"]).toLocaleString('en-GB', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : "-"}
                          </MDTypography>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {!isInventoryViewOnly && !record.isInitial && (
                              <Tooltip title="Edit Record">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditHistoryModal(record)}
                                  sx={{
                                    padding: "4px",
                                    color: "#7b809a",
                                    "&:hover": { color: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.05)" }
                                  }}
                                >
                                  <span className="material-icons" style={{ fontSize: "16px" }}>edit</span>
                                </IconButton>
                              </Tooltip>
                            )}
                            {record.fields["Attachments"] && record.fields["Attachments"].length > 0 && (
                              <MDBox display="flex" gap={0.5}>
                                {record.fields["Attachments"].slice(0, 3).map((file, i) => (
                                  <AuthenticatedImage
                                    key={i}
                                    src={file.presignedUrl || file.thumbnails?.small?.url || file.url}
                                    alt={file.name}
                                    onClick={() => {
                                      const allUrls = record.fields["Attachments"].map(a => a.presignedUrl || a.url);
                                      handleOpenPreviewModal(allUrls, i);
                                    }}
                                    style={{
                                      width: 24, height: 24, borderRadius: "4px", cursor: "pointer",
                                      border: "1px solid #f0f2f5", transition: "0.2s"
                                    }}
                                  />
                                ))}
                                {record.fields["Attachments"].length > 3 && (
                                  <MDTypography variant="caption" sx={{ alignSelf: 'center', ml: 0.5, color: "#1A73E8", fontWeight: "bold" }}>
                                    +{record.fields["Attachments"].length - 3}
                                  </MDTypography>
                                )}
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #f0f2f5" }}>
          <MDButton
            onClick={handleCloseHistoryModal}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
              borderColor: "#d2d6da",
              color: "#344767"
            }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Found/Back Modal */}
      <Dialog
        open={isFoundModalOpen}
        onClose={handleCloseFoundModal}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "400px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Item Found: {selectedItemForFound?.fields["Item Name"]}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Username Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              REPORTED BY
            </MDTypography>
            <TextField
              value={user?.name || user?.username || "System"}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* ID Field (Auto) - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID
            </MDTypography>
            <TextField
              value={nextHistoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Recorded at Field (Auto) */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RECORDED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Description Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              DESCRIPTION
            </MDTypography>
            <TextField
              value={foundDescription}
              onChange={(e) => setFoundDescription(e.target.value)}
              placeholder="Enter found details"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachments Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#4CAF50", backgroundColor: "rgba(76, 175, 80, 0.02)" }
              }}
              onClick={() => document.getElementById("found-files").click()}
            >
              <input
                id="found-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setFoundAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#4CAF50" }}>task_alt</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Add Found Images
              </MDTypography>
            </MDBox>

            {foundAttachments.length > 0 && (
              <MDBox mt={1.5} sx={{ maxHeight: "120px", overflowY: "auto" }}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="caption" fontWeight="bold">{foundAttachments.length} files</MDTypography>
                  <MDTypography variant="caption" color="error" sx={{ cursor: "pointer" }} onClick={() => setFoundAttachments([])}>Clear</MDTypography>
                </MDBox>
                {foundAttachments.map((file, idx) => (
                  <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa" }}>
                    <MDTypography variant="caption" noWrap sx={{ maxWidth: "80%" }}>{file.name}</MDTypography>
                    <span className="material-icons" style={{ fontSize: "14px", color: "#F44336", cursor: "pointer" }} onClick={() => setFoundAttachments(foundAttachments.filter((_, i) => i !== idx))}>close</span>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "16px", borderTop: "none" }}>
          <MDButton onClick={handleCloseFoundModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveFoundBack}
            color="success"
            variant="gradient"
            disabled={isSubmittingFound}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingFound ? "Confirming..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Send Repair Modal */}
      <Dialog
        open={isRepairModalOpen}
        onClose={handleCloseRepairModal}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "400px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Send to Repair: {selectedItemForRepair?.fields["Item Name"]}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Username Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              REPORTED BY
            </MDTypography>
            <TextField
              value={user?.name || user?.username || "System"}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* ID Field (Auto) - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID
            </MDTypography>
            <TextField
              value={nextHistoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Recorded at Field (Auto) */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RECORDED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Price Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              PRICE
            </MDTypography>
            <TextField
              type="number"
              value={repairPrice}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || Number(val) >= 0) {
                  setRepairPrice(val);
                }
              }}
              inputProps={{ min: 0 }}
              placeholder="Enter repair cost"
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  "& fieldset": { borderColor: "#d2d6da" },
                },
                "& input": {
                  color: "#344767",
                  fontSize: "0.875rem",
                }
              }}
            />
          </MDBox>

          {/* Send To Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              SEND TO
            </MDTypography>
            <TextField
              value={repairSendTo}
              onChange={(e) => setRepairSendTo(e.target.value)}
              placeholder="Where are you sending it?"
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  "& fieldset": { borderColor: "#d2d6da" },
                },
                "& input": {
                  color: "#344767",
                  fontSize: "0.875rem",
                }
              }}
            />
          </MDBox>

          {/* Description Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              DESCRIPTION
            </MDTypography>
            <TextField
              value={repairDescription}
              onChange={(e) => setRepairDescription(e.target.value)}
              placeholder="Enter repair details"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachments Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.02)" }
              }}
              onClick={() => document.getElementById("repair-files").click()}
            >
              <input
                id="repair-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setRepairAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#1A73E8" }}>collections</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Add Repair Images
              </MDTypography>
            </MDBox>

            {repairAttachments.length > 0 && (
              <MDBox mt={1.5} sx={{ maxHeight: "120px", overflowY: "auto" }}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="caption" fontWeight="bold">{repairAttachments.length} files</MDTypography>
                  <MDTypography variant="caption" color="error" sx={{ cursor: "pointer" }} onClick={() => setRepairAttachments([])}>Clear</MDTypography>
                </MDBox>
                {repairAttachments.map((file, idx) => (
                  <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa" }}>
                    <MDTypography variant="caption" noWrap sx={{ maxWidth: "80%" }}>{file.name}</MDTypography>
                    <span className="material-icons" style={{ fontSize: "14px", color: "#F44336", cursor: "pointer" }} onClick={() => setRepairAttachments(repairAttachments.filter((_, i) => i !== idx))}>close</span>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "16px", borderTop: "none" }}>
          <MDButton onClick={handleCloseRepairModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveRepairReport}
            color="info"
            variant="gradient"
            disabled={isSubmittingRepair}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingRepair ? "Confirming..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Replace Modal */}
      <Dialog
        open={isReplaceModalOpen}
        onClose={handleCloseReplaceModal}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "400px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Replace Item: {selectedItemForReplace?.fields["Item Name"]}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Username Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              REPORTED BY
            </MDTypography>
            <TextField
              value={user?.name || user?.username || "System"}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* ID Field (Auto) - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID
            </MDTypography>
            <TextField
              value={nextHistoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Recorded at Field (Auto) */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RECORDED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* New Item Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              NEW ITEM NAME
            </MDTypography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={replacementItemName}
              onChange={(e) => setReplacementItemName(e.target.value)}
              placeholder="Enter new item name"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  "& fieldset": { borderColor: "#d2d6da" },
                },
                "& input": {
                  color: "#344767",
                  fontSize: "0.875rem",
                }
              }}
            />
          </MDBox>

          {/* Status Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              STATUS
            </MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={replaceStatus}
                onChange={(e) => setReplaceStatus(e.target.value)}
                sx={{
                  height: "40px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                }}
              >
                <MenuItem value="Good">Good</MenuItem>

                <MenuItem value="Misplaced">Misplaced</MenuItem>
              </Select>
            </FormControl>
          </MDBox>

          {/* Description Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              DESCRIPTION
            </MDTypography>
            <TextField
              value={replaceDescription}
              onChange={(e) => setReplaceDescription(e.target.value)}
              placeholder="Enter replacement details"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachments Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.02)" }
              }}
              onClick={() => document.getElementById("replace-files").click()}
            >
              <input
                id="replace-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setReplaceAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#1A73E8" }}>add_a_photo</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Add Replacement Images
              </MDTypography>
            </MDBox>

            {replaceAttachments.length > 0 && (
              <MDBox mt={1.5} sx={{ maxHeight: "120px", overflowY: "auto" }}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="caption" fontWeight="bold">{replaceAttachments.length} files</MDTypography>
                  <MDTypography variant="caption" color="error" sx={{ cursor: "pointer" }} onClick={() => setReplaceAttachments([])}>Clear</MDTypography>
                </MDBox>
                {replaceAttachments.map((file, idx) => (
                  <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa" }}>
                    <MDTypography variant="caption" noWrap sx={{ maxWidth: "80%" }}>{file.name}</MDTypography>
                    <span className="material-icons" style={{ fontSize: "14px", color: "#F44336", cursor: "pointer" }} onClick={() => setReplaceAttachments(replaceAttachments.filter((_, i) => i !== idx))}>close</span>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "16px", borderTop: "none" }}>
          <MDButton onClick={handleCloseReplaceModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveReplaceReport}
            color="info"
            variant="gradient"
            disabled={isSubmittingReplace}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingReplace ? "Confirming..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isMisplacedModalOpen}
        onClose={handleCloseMisplacedModal}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "400px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ paddingBottom: "16px", paddingTop: "8px", fontWeight: "bold", color: "#344767", borderBottom: "none" }}>
          Report Misplaced Item
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "12px !important", display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Username Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              REPORTED BY
            </MDTypography>
            <TextField
              value={user?.name || user?.username || "System"}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* ID Field (Auto) - Hidden */}
          <MDBox display="none">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ID
            </MDTypography>
            <TextField
              value={nextHistoryId}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Recorded at Field (Auto) */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RECORDED AT
            </MDTypography>
            <TextField
              value={getPakistanTime()}
              disabled
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": { color: "#7b809a", WebkitTextFillColor: "#7b809a" },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa", borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Reservation ID Dropdown - Dynamic */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              RESERVATION ID
            </MDTypography>
            <Select
              value={misplacedReservationId}
              onChange={(e) => setMisplacedReservationId(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">
                {isFetchingReservations ? "Loading reservations..." : recentReservations.length === 0 ? "No active reservations found" : "Select Reservation"}
              </MenuItem>
              {recentReservations.map((res) => (
                <MenuItem key={res.id} value={String(res.fields["Reservation ID"])}>
                  {res.fields["Guest Name"] ? `${res.fields["Guest Name"]} (${res.fields["Reservation ID"]})` : res.fields["Reservation ID"]}
                </MenuItem>
              ))}
            </Select>
          </MDBox>

          {/* Description Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "4px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              DESCRIPTION
            </MDTypography>
            <TextField
              value={misplacedDescription}
              onChange={(e) => setMisplacedDescription(e.target.value)}
              placeholder="Enter misplaced details"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
          </MDBox>

          {/* Attachments Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, marginBottom: "8px", fontSize: "0.65rem", letterSpacing: "1px" }}>
              ATTACHMENTS
            </MDTypography>
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#FF9800", backgroundColor: "rgba(255, 152, 0, 0.02)" }
              }}
              onClick={() => document.getElementById("misplaced-files").click()}
            >
              <input
                id="misplaced-files"
                type="file"
                hidden
                multiple
                accept=".jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter(f => f.type === "image/jpeg" || f.type === "image/png" || /\.(jpg|jpeg|png)$/i.test(f.name));
                  if (validFiles.length !== files.length) {
                    showMessage("Only JPG and PNG images are allowed.", "error");
                  }
                  setMisplacedAttachments(prev => {
                    const existingNames = new Set(prev.map(f => f.name.toLowerCase()));
                    const unique = validFiles.filter(f => {
                      if (existingNames.has(f.name.toLowerCase())) {
                        showMessage(`"${f.name}" is already attached.`, "error");
                        return false;
                      }
                      return true;
                    });
                    return [...prev, ...unique];
                  });
                  e.target.value = null; // reset input
                }}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#FF9800" }}>image_search</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Add Misplaced Images
              </MDTypography>
            </MDBox>

            {misplacedAttachments.length > 0 && (
              <MDBox mt={1.5} sx={{ maxHeight: "120px", overflowY: "auto" }}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="caption" fontWeight="bold">{misplacedAttachments.length} files</MDTypography>
                  <MDTypography variant="caption" color="error" sx={{ cursor: "pointer" }} onClick={() => setMisplacedAttachments([])}>Clear</MDTypography>
                </MDBox>
                {misplacedAttachments.map((file, idx) => (
                  <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa" }}>
                    <MDTypography variant="caption" noWrap sx={{ maxWidth: "80%" }}>{file.name}</MDTypography>
                    <span className="material-icons" style={{ fontSize: "14px", color: "#F44336", cursor: "pointer" }} onClick={() => setMisplacedAttachments(misplacedAttachments.filter((_, i) => i !== idx))}>close</span>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "16px", borderTop: "none" }}>
          <MDButton onClick={handleCloseMisplacedModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveMisplacedReport}
            color="warning"
            variant="gradient"
            disabled={isSubmittingMisplaced}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingMisplaced ? "Confirming..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ====== EDIT ITEM MODAL ====== */}
      <Dialog
        open={isEditItemModalOpen}
        onClose={handleCloseItemEditModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            width: { xs: "95%", sm: "550px" },
            margin: { xs: "16px auto", sm: "32px auto" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#344767" }}>
          Edit Item Details
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          {editItemError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                "& .MuiAlert-message": { width: "100%", overflow: "auto", maxHeight: "150px" }
              }}
            >
              <strong>Teable API Error:</strong>
              <MDBox component="pre" sx={{ mt: 1, fontSize: "10px" }}>
                {editItemError}
              </MDBox>
            </Alert>
          )}

          {/* Property / Listing Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>PROPERTY *</MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={editItemListingName}
                onChange={(e) => setEditItemListingName(e.target.value)}
                sx={{ height: "40px", borderRadius: "8px" }}
              >
                {apartments.map((name, index) => (
                  <MenuItem key={index} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>

          {/* Item Name Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>ITEM NAME *</MDTypography>
            <TextField
              value={editItemName}
              onChange={(e) => setEditItemName(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </MDBox>

          {/* Category Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>CATEGORY *</MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={editItemCategory}
                onChange={(e) => setEditItemCategory(e.target.value)}
                sx={{ height: "40px", borderRadius: "8px" }}
              >
                {categories.map((cat, index) => (
                  <MenuItem key={index} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>

          {/* Quantity & Cost Row */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>QUANTITY *</MDTypography>
                <TextField
                  type="number"
                  value={editItemQuantity}
                  onChange={(e) => setEditItemQuantity(e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>COST (PKR)</MDTypography>
                <TextField
                  type="number"
                  value={editItemCost}
                  onChange={(e) => setEditItemCost(e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </MDBox>
            </Grid>
          </Grid>

          {/* Attachments Section */}
          <MDBox display="flex" flexDirection="column" mt={2}>
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>ATTACHMENTS</MDTypography>

            {/* Existing Attachments */}
            {editItemAttachments.length > 0 && (
              <MDBox mb={2} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {editItemAttachments.map((file) => (
                  <MDBox
                    key={file.id}
                    sx={{
                      position: "relative",
                      width: "60px",
                      height: "60px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #eee"
                    }}
                  >
                    <AuthenticatedImage
                      src={file.smThumbnailUrl || file.presignedUrl || file.url}
                      alt={file.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                      onClick={() => handleOpenPreviewModal(editItemAttachments.map(a => a.presignedUrl || a.url), editItemAttachments.indexOf(file))}
                    />
                    <MDBox
                      onClick={() => handleRemoveEditItemExistingAttachment(file.id)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        width: "18px",
                        height: "18px",
                        backgroundColor: "rgba(255, 67, 54, 0.9)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: "14px", color: "#fff" }}>close</span>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            )}

            {/* Dropzone/Upload Button */}
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.02)" }
              }}
              onClick={() => document.getElementById("edit-item-files").click()}
            >
              <input
                id="edit-item-files"
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleEditItemFileChange}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#1A73E8" }}>cloud_upload</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Upload New Images
              </MDTypography>
            </MDBox>

            {/* Selected New Files */}
            {editItemNewFiles.length > 0 && (
              <MDBox mt={1.5}>
                <MDTypography variant="caption" fontWeight="bold">New files ({editItemNewFiles.length}):</MDTypography>
                <MDBox sx={{ mt: 0.5, maxHeight: "100px", overflowY: "auto" }}>
                  {editItemNewFiles.map((file, idx) => (
                    <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                      <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {file.name}
                      </MDTypography>
                      <IconButton size="small" onClick={() => handleRemoveEditItemNewFile(idx)}>
                        <span className="material-icons" style={{ fontSize: "14px", color: "#F44336" }}>close</span>
                      </IconButton>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ gap: 1, p: 3 }}>
          <MDButton onClick={handleCloseItemEditModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveItemEdit}
            color="info"
            variant="gradient"
            disabled={isSubmittingEditItem}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingEditItem ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ====== EDIT HISTORY RECORD MODAL ====== */}
      <Dialog
        open={isEditHistoryModalOpen}
        onClose={handleCloseEditHistoryModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: { xs: "24px", md: "16px" },
            padding: { xs: "16px", md: "16px" },
            width: { xs: "95%", sm: "550px" },
            margin: { xs: "16px auto", md: "auto" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#344767" }}>
          Edit History Record
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          {editHistoryError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                "& .MuiAlert-message": { width: "100%", overflow: "auto", maxHeight: "150px" }
              }}
            >
              <strong>Teable API Error (400):</strong>
              <MDBox component="pre" sx={{ mt: 1, fontSize: "10px" }}>
                {editHistoryError}
              </MDBox>
            </Alert>
          )}

          {/* Status Field */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>STATUS</MDTypography>
            <FormControl fullWidth size="small">
              <Select
                value={editHistoryStatus}
                onChange={(e) => setEditHistoryStatus(e.target.value)}
                sx={{ height: "40px", borderRadius: "8px" }}
              >
                <MenuItem value="Good">Good</MenuItem>
                <MenuItem value="Damaged">Damaged</MenuItem>
                <MenuItem value="Misplaced">Misplaced</MenuItem>
                <MenuItem value="In Repair">In Repair</MenuItem>
                <MenuItem value="Replaced">Replaced</MenuItem>
              </Select>
            </FormControl>
          </MDBox>

          {/* Description Field - Always Shown */}
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>DESCRIPTION</MDTypography>
            <TextField
              multiline
              rows={3}
              value={editHistoryDescription}
              onChange={(e) => setEditHistoryDescription(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </MDBox>



          {/* Price Field - Shown for In Repair / Replaced */}
          {(editHistoryStatus === "In Repair" || editHistoryStatus === "Replaced") && (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>PRICE (PKR)</MDTypography>
              <TextField
                type="number"
                value={editHistoryPrice}
                onChange={(e) => setEditHistoryPrice(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </MDBox>
          )}

          {/* Send To Field - Shown for In Repair / Replaced */}
          {(editHistoryStatus === "In Repair" || editHistoryStatus === "Replaced") && (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>SEND TO</MDTypography>
              <TextField
                value={editHistorySendTo}
                onChange={(e) => setEditHistorySendTo(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </MDBox>
          )}

          {/* Reservation ID Field - Shown for Damaged / Misplaced / Replaced */}
          {(editHistoryStatus === "Damaged" || editHistoryStatus === "Misplaced" || editHistoryStatus === "Replaced") && (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>RESERVATION ID</MDTypography>
              <Select
                value={editHistoryReservationId}
                onChange={(e) => setEditHistoryReservationId(e.target.value)}
                displayEmpty
                fullWidth
                size="small"
                sx={{ borderRadius: "8px" }}
              >
                <MenuItem value="">
                  {isFetchingReservations ? "Loading reservations..." : recentReservations.length === 0 ? "No active reservations found" : "Select Reservation"}
                </MenuItem>
                {recentReservations.map((res) => (
                  <MenuItem key={res.id} value={String(res.fields["Reservation ID"])}>
                    {res.fields["Guest Name"] ? `${res.fields["Guest Name"]} (${res.fields["Reservation ID"]})` : res.fields["Reservation ID"]}
                  </MenuItem>
                ))}
              </Select>
            </MDBox>
          )}

          {/* Attachments Section - Moved to end */}
          <MDBox display="flex" flexDirection="column" mt={2}>
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>ATTACHMENTS</MDTypography>

            {/* Existing Attachments */}
            {editHistoryAttachments.length > 0 && (
              <MDBox mb={2} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {editHistoryAttachments.map((file) => (
                  <MDBox
                    key={file.id}
                    sx={{
                      position: "relative",
                      width: "60px",
                      height: "60px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #eee"
                    }}
                  >
                    <AuthenticatedImage
                      src={file.smThumbnailUrl || file.presignedUrl}
                      alt={file.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                      onClick={() => handleOpenPreviewModal(editHistoryAttachments.map(a => a.presignedUrl || a.url), editHistoryAttachments.indexOf(file))}
                    />
                    <MDBox
                      onClick={() => handleRemoveEditHistoryExistingAttachment(file.id)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        width: "18px",
                        height: "18px",
                        backgroundColor: "rgba(255, 67, 54, 0.9)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: "14px", color: "#fff" }}>close</span>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            )}

            {/* Dropzone/Upload Button */}
            <MDBox
              sx={{
                border: "1px dashed #d2d6da",
                borderRadius: "8px",
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.02)" }
              }}
              onClick={() => document.getElementById("edit-history-files").click()}
            >
              <input
                id="edit-history-files"
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleEditHistoryFileChange}
              />
              <span className="material-icons" style={{ fontSize: "24px", color: "#1A73E8" }}>cloud_upload</span>
              <MDTypography variant="button" fontWeight="medium" display="block" color="text">
                Upload New Images
              </MDTypography>
            </MDBox>

            {/* Selected New Files */}
            {editHistoryNewFiles.length > 0 && (
              <MDBox mt={1.5}>
                <MDTypography variant="caption" fontWeight="bold">New files ({editHistoryNewFiles.length}):</MDTypography>
                <MDBox sx={{ mt: 0.5, maxHeight: "100px", overflowY: "auto" }}>
                  {editHistoryNewFiles.map((file, idx) => (
                    <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.25} mb={0.5} sx={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                      <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {file.name}
                      </MDTypography>
                      <IconButton size="small" onClick={() => handleRemoveEditHistoryNewFile(idx)}>
                        <span className="material-icons" style={{ fontSize: "14px", color: "#F44336" }}>close</span>
                      </IconButton>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            )}
          </MDBox>

        </DialogContent>
        <DialogActions sx={{ padding: "24px", paddingTop: "8px", borderTop: "none", gap: 2, justifyContent: "flex-end" }}>
          <MDButton
            onClick={handleCloseEditHistoryModal}
            sx={{
              color: "#7b809a",
              border: "1.5px solid #d2d6da",
              textTransform: "uppercase",
              fontWeight: "bold",
              fontSize: "0.75rem",
              px: 4,
              py: 1,
              borderRadius: "10px",
              backgroundColor: "transparent",
              '&:hover': { backgroundColor: "#f8f9fa", borderColor: "#7b809a" }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveEditHistory}
            disabled={isSubmittingEditHistory}
            sx={{
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              fontWeight: "bold",
              px: 4,
              py: 1,
              borderRadius: "10px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
            }}
          >
            {isSubmittingEditHistory ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ====== MANAGE CATEGORIES MODAL ====== */}
      <Dialog
        open={isManageCategoriesModalOpen}
        onClose={handleCloseManageCategoriesModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            width: { xs: "95%", sm: "500px" },
            margin: { xs: "16px auto", sm: "32px auto" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#344767", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Manage Categories
          <IconButton onClick={handleCloseManageCategoriesModal} size="small">
            <span className="material-icons">close</span>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <MDBox display="flex" flexDirection="column" gap={1}>
            {categoryRecords.length === 0 ? (
              <MDTypography variant="button" color="text">No categories found.</MDTypography>
            ) : (
              categoryRecords.map((record) => (
                <Card key={record.id} sx={{ p: 1.5, display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", border: "1px solid #f0f2f5" }}>
                  <MDTypography variant="button" fontWeight="medium">
                    {record.fields["Name"]}
                  </MDTypography>
                  <MDBox>
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditCategoryModal(record)}
                        sx={{ color: "#7b809a", "&:hover": { color: "#1A73E8" } }}
                      >
                        <span className="material-icons" style={{ fontSize: "18px" }}>edit</span>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </Card>
              ))
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseManageCategoriesModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ====== EDIT CATEGORY MODAL ====== */}
      <Dialog
        open={isEditCategoryModalOpen}
        onClose={handleCloseEditCategoryModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            color: "#344767",
            borderRadius: "16px",
            padding: "16px",
            width: { xs: "95%", sm: "400px" },
            margin: { xs: "16px auto", sm: "32px auto" },
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#344767" }}>
          Edit Category
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="overline" color="text" sx={{ opacity: 0.8, mb: 0.5 }}>CATEGORY NAME *</MDTypography>
            <TextField
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
              autoFocus
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <MDButton onClick={handleCloseEditCategoryModal} color="secondary" variant="outlined" sx={{ borderRadius: "8px" }}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveEditCategory}
            color="info"
            variant="gradient"
            disabled={isSubmittingEditCategory}
            sx={{ borderRadius: "8px" }}
          >
            {isSubmittingEditCategory ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Global Toaster for showMessage */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity || "info"} sx={{ width: '100%', borderRadius: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }} variant="standard">
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container >
  );
}

export default Inventory;
